import { useState, useRef, useEffect } from 'react';
import { toBlob } from 'html-to-image';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Camera, Upload, RefreshCw, Sparkles, AlertCircle, Trash2, Smile, Zap, X, Calendar, Send, Settings, Download, Phone, Navigation, SquareArrowOutUpRight, ChevronLeft, ChevronRight, SwitchCamera } from 'lucide-react';
import guideImage from './images/guide.jpg';
import SettingsPanel from './settings';

const DEFAULT_TRANSFORM_TYPE = 1;
const TRANSFORM_DURATION_SECONDS = 120;
const SMILE_SESSION_TIMEOUT_MS = 30 * 60 * 1000;

const TRANSFORM_ANALYSIS_STAGES = [
    { label: 'Starting the transformation', detail: 'Bootstrapping the smile simulation workflow.' },
    { label: 'Procuring the images', detail: 'Preparing your uploaded photo for the analysis engine.' },
    { label: 'Identifying the face', detail: 'Locating facial landmarks and smile framing.' },
    { label: 'Identifying teeth and expression', detail: 'Reading tooth position, angulation, and expression dynamics.' },
    { label: 'Analyzing the process around', detail: 'Reviewing surrounding structures that influence the smile plan.' },
    { label: 'Periodontist analysis', detail: 'Checking gum line, periodontal support, and tissue harmony.' },
    { label: 'Orthodontist analysis', detail: 'Evaluating alignment, spacing, and bite-related movement needs.' },
    { label: 'Prosthodontist analysis', detail: 'Assessing restorative design possibilities for shape and replacement.' },
    { label: 'Denturist analysis', detail: 'Reviewing appliance-based and denture-oriented smile restoration paths.' },
    { label: 'Final analysis for cost and timeline', detail: 'Summarizing likely treatment complexity, timelines, and fee bands.' },
    { label: 'Finalizing the image', detail: 'Packaging the transformed concepts and assessment results.' }
];

const APPOINTMENT_TIME_INTERVALS = [
    'Morning 9 AM to 12 PM',
    'Afternoon 12 PM to 4 PM',
    'Evening 4 PM to 7 PM'
];

const WhatsAppLogo = ({ size = 26, className = '' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
        className={className}
    >
        <path
            fill="currentColor"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.03 3.2C9.02 3.2 3.33 8.82 3.33 15.75c0 2.3.64 4.54 1.84 6.49L3.2 29l6.98-1.83a12.86 12.86 0 0 0 5.85 1.42c7 0 12.7-5.62 12.7-12.54S23.03 3.2 16.03 3.2Zm0 22.98c-1.87 0-3.7-.5-5.3-1.45l-.38-.23-4.13 1.08 1.1-4.01-.25-.4a10.1 10.1 0 0 1-1.55-5.42c0-5.62 4.72-10.2 10.51-10.2 5.8 0 10.51 4.58 10.51 10.2s-4.72 10.43-10.51 10.43Zm5.76-7.64c-.31-.16-1.86-.91-2.15-1.01-.29-.11-.5-.16-.71.16-.21.31-.81 1.01-1 1.22-.18.21-.37.23-.68.08-.31-.16-1.32-.48-2.52-1.53-.93-.82-1.56-1.84-1.74-2.15-.18-.31-.02-.48.14-.63.14-.14.31-.37.47-.55.16-.18.21-.31.31-.52.1-.21.05-.39-.03-.55-.08-.16-.71-1.68-.97-2.3-.25-.6-.51-.52-.71-.53h-.6c-.21 0-.55.08-.84.39-.29.31-1.1 1.06-1.1 2.59 0 1.53 1.13 3 1.29 3.21.16.21 2.23 3.36 5.4 4.71.75.32 1.34.51 1.8.65.76.24 1.45.21 1.99.13.61-.09 1.86-.75 2.12-1.48.26-.73.26-1.35.18-1.48-.08-.13-.29-.21-.61-.37Z"
        />
    </svg>
);

const FalconSmileAppointmentProposal = ({ config, reviewMode = false, onAnalysisReady = null }) => {
    // Environment provides API key at runtime.
    const API_KEY = config.apiKey;
    const FRAME_ORIGIN = window.location.origin;
    const PARENT_ORIGIN = config.parentOrigin || FRAME_ORIGIN;
    const WEBSOCKET_URL = "wss://ltcyn3wz1i.execute-api.us-west-2.amazonaws.com/production";
    const HTTP_API_BASE_URL = "https://nrbvpc4nke.execute-api.us-west-2.amazonaws.com";
    const STOCK_IMAGES_BUCKET_URL = "https://dental-live-ai-stock-images.s3.us-west-2.amazonaws.com/";
    const STOCK_IMAGES_LIST_URL = `${STOCK_IMAGES_BUCKET_URL}?list-type=2`;
    const getDomainNameFromOrigin = (originValue) => {
        const rawOrigin = String(originValue || '').trim();
        if (!rawOrigin) return 'default';

        try {
            const url = new URL(rawOrigin.includes('://') ? rawOrigin : `https://${rawOrigin}`);
            return url.hostname.replace(/^www\./i, '').toLowerCase() || 'default';
        } catch {
            return rawOrigin
                .replace(/^https?:\/\//i, '')
                .split('/')[0]
                .replace(/^www\./i, '')
                .toLowerCase() || 'default';
        }
    };
    const CURRENT_DOMAIN_NAME = getDomainNameFromOrigin(PARENT_ORIGIN);

    const [image, setImage] = useState(null);
    const [maskCoordinates, setMaskCoordinates] = useState(null);
    const [visualOverlayBase64Image, setVisualOverlayBase64Image] = useState(null);
    const [specificInstructions, setSpecificInstructions] = useState("");
    const [isCapturing, setIsCapturing] = useState(false);
    const [cameraDevices, setCameraDevices] = useState([]);
    const [activeCameraDeviceId, setActiveCameraDeviceId] = useState('');
    const [cameraFacingMode, setCameraFacingMode] = useState('user');
    const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isPreparingDownload, setIsPreparingDownload] = useState(false);
    const [generationProgress, setGenerationProgress] = useState({
        step: 'idle',
        uploadProgress: 0
    });
    const [generationDetailsOpen, setGenerationDetailsOpen] = useState(false);
    // reference to satisfy some linters that may not detect usage inside JSX handlers
    void generationDetailsOpen; void setGenerationDetailsOpen;
    const generationDetailsContainerRef = useRef(null);
    const generationDetailsInnerRef = useRef(null);
    const [generationDetailsMeasuredHeight, setGenerationDetailsMeasuredHeight] = useState(0);
    const [transformTimeLeft, setTransformTimeLeft] = useState(TRANSFORM_DURATION_SECONDS);
    const [results, setResults] = useState([]);
    const [triageData, setTriageData] = useState(null);
    const [estimates, setEstimates] = useState(null);
    const [patientData, setPatientData] = useState(null);
    const [error, setError] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(reviewMode ? false : true);
    const [retryableError, setRetryableError] = useState(null);
    const [showStockImagePicker, setShowStockImagePicker] = useState(false);
    const [stockImages, setStockImages] = useState([]);
    const [stockImagesLoading, setStockImagesLoading] = useState(false);
    const [stockImagesError, setStockImagesError] = useState('');
    const [selectedStockImageUrl, setSelectedStockImageUrl] = useState('');

    // Chat Prompts & Constants
    const initialChatMessages = [
        { role: 'bot', text: "Hello! I’m your AI Smile Concierge. I’m here to help you understand your AI Smile Transformation, answer your questions, and explore possible next steps. What would you like to know?" }
    ];
    const initialUserData = {
        name: '',
        contact: '',
        phone: '',
        age: 25,
        dob: '',
        gender: 'not_say',
        slots: [],
        triage: { specialists: [] }
    };
    useEffect(() => {
        const measure = () => {
            if (generationDetailsInnerRef.current) {
                setGenerationDetailsMeasuredHeight(generationDetailsInnerRef.current.scrollHeight || 0);
            }
        };

        if (generationDetailsOpen) measure();
        const onResize = () => measure();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [generationDetailsOpen, generationProgress.step, generationProgress.uploadProgress, transformTimeLeft, results]);

    // Defaults and shared refs/states used across the component
    const defaultThemeSettings = {
        screenBg: '#0f1724',
        screenText: '#e6eef8',
        primaryBg: '#ffffff',
        primaryText: '#0f1724',
        highlightBg: '#06b6d4',
        highlightText: '#ffffff',
        warningBg: '#f97316',
        warningText: '#ffffff',
        infoBg: '#e0f2fe',
        infoText: '#0369a1',
        border: '#e5e7eb',
        baseTextSize: 16,
        fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
        heroTitle: 'Preview your smile',
        heroSubtitle: 'AI powered smile simulation',
        clinicLocations: [],
        clinicInstructions: '',
        showSpecificInstructions: true,
        showFixedAppointment: true,
        showLocations: true,
        showAIConcierge: true,
        showAssessmentSpecialists: false,
        showAssessmentPrice: false,
        showAssessmentTimeline: false,
        showAssessmentEstimateCards: false,
        showAIConsultation: false
    };

    const [themeSettings, setThemeSettings] = useState(defaultThemeSettings);
    const [draftThemeSettings, setDraftThemeSettings] = useState(defaultThemeSettings);

    const notifyParentHeightRef = useRef(() => { });
    const socketRef = useRef(null);
    const connectionPromiseRef = useRef(null);
    const timeoutMapRef = useRef({});
    const iframeContentRef = useRef(null);

    const specificInstructionsRef = useRef(specificInstructions);
    const pendingResizedBlobRef = useRef(null);
    const uploadedImageUrlRef = useRef('');
    const pendingAppointmentSlotsRef = useRef([]);
    const pendingTransformationResultsRef = useRef([]);
    const pendingChatTextRef = useRef('');
    const pendingChatRequestRef = useRef(null);
    const chatRetryCountRef = useRef(0);
    const chatRetryTimeoutRef = useRef(null);
    const patientDataRef = useRef(null);
    const smileSessionTimeoutRef = useRef(null);

    const [showSettingsPanel, setShowSettingsPanel] = useState(false);
    const [isSavingTheme, setIsSavingTheme] = useState(false);
    const fontOptions = [
        { label: 'Inter', value: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial' },
        { label: 'System', value: 'system-ui' }
    ];

    const initialPreferredSlots = [{ day: '', time: '' }, { day: '', time: '' }, { day: '', time: '' }];
    const getDefaultPreferredSlots = (days = [], hours = APPOINTMENT_TIME_INTERVALS) => {
        if (!days.length) return initialPreferredSlots.map(slot => ({ ...slot }));

        const preferredDefaultDays = ['Monday', 'Tuesday', 'Wednesday'];
        const defaultTime = hours[0] || '';
        const fallbackDays = days.slice(0, 3);

        return initialPreferredSlots.map((slot, index) => {
            const defaultDayCandidate = preferredDefaultDays[index];
            const fallbackDay = fallbackDays[index] || days[index % days.length] || days[0] || '';
            const resolvedDefaultDay = days.includes(defaultDayCandidate) ? defaultDayCandidate : fallbackDay;

            return {
                day: slot.day || resolvedDefaultDay,
                time: slot.time || defaultTime
            };
        });
    };
    const [preferredSlots, setPreferredSlots] = useState(initialPreferredSlots);

    const [userData, setUserData] = useState(initialUserData);
    const [chatMessages, setChatMessages] = useState(initialChatMessages);
    const [userInput, setUserInput] = useState('');
    const [isChatting, setIsChatting] = useState(false);
    const chatEndRef = useRef(null);

    const [consentChecked, setConsentChecked] = useState(false);
    const [consentFormError, setConsentFormError] = useState('');
    const [pendingImageAction, setPendingImageAction] = useState(null);
    const [isConfirmingAppointment, setIsConfirmingAppointment] = useState(false);
    const [bookingFlow, setBookingFlow] = useState('idle');
    const isBookingFormRequested = bookingFlow === 'form';
    const bookingSectionRef = useRef(null);
    const [selectedLocationIndex, setSelectedLocationIndex] = useState(0);

    const assessmentRef = useRef(null);
    const videoRef = useRef(null);
    const fileInputRef = useRef(null);
    const [clinicData, setClinicData] = useState(null);
    const [appointmentFormError, setAppointmentFormError] = useState('');
    const [viewerImage, setViewerImage] = useState(null);
    const [selectedSmileImageKey, setSelectedSmileImageKey] = useState('original');
    const currentThemeSettings = showSettingsPanel ? draftThemeSettings : themeSettings;

    const draftThemeSettingsRef = useRef(draftThemeSettings);
    const themeSettingsRef = useRef(themeSettings);
    const userDataRef = useRef(userData);
    const guideModalRef = useRef(null);
    // ensure refs/values are considered used (silence some linters)
    void generationDetailsContainerRef; void generationDetailsMeasuredHeight;
    const [mapZoomLevels, setMapZoomLevels] = useState({});
    const [connectionRetryError, setConnectionRetryError] = useState(null);
    const activeThemeSettings = showSettingsPanel ? draftThemeSettings : themeSettings;
    const showPersonalizedAssessment = (activeThemeSettings.showAssessmentSpecialists || activeThemeSettings.showAssessmentPrice || activeThemeSettings.showAssessmentTimeline);
    const showAIConsultation = !reviewMode && activeThemeSettings.showAIConsultation;
    const showFixedAppointment = !reviewMode && activeThemeSettings.showFixedAppointment !== false;
    const showLocations = !reviewMode && activeThemeSettings.showLocations !== false;
    const showAppointmentForm = showFixedAppointment && isBookingFormRequested;
    const scrollToTopOfApp = (behavior = 'smooth') => {
        if (typeof window === 'undefined') return;
        try {
            window.scrollTo({ top: 0, behavior });
        } catch {
            window.scrollTo(0, 0);
        }
    };

    const themeStyle = {
        '--falcon-screen': activeThemeSettings.screenBg,
        '--falcon-screen-text': activeThemeSettings.screenText,
        '--falcon-primary': activeThemeSettings.primaryBg,
        '--falcon-primary-text': activeThemeSettings.primaryText,
        '--falcon-highlight': activeThemeSettings.highlightBg,
        '--falcon-highlight-text': activeThemeSettings.highlightText,
        '--falcon-warning': activeThemeSettings.warningBg,
        '--falcon-warning-text': activeThemeSettings.warningText,
        '--falcon-info': activeThemeSettings.infoBg,
        '--falcon-info-text': activeThemeSettings.infoText,
        '--falcon-border': activeThemeSettings.border,
        '--falcon-base-text-size': `${activeThemeSettings.baseTextSize}px`,
        fontFamily: activeThemeSettings.fontFamily,
        backgroundColor: activeThemeSettings.screenBg
    };
    const openSettingsPanel = () => {
        setDraftThemeSettings(themeSettings);
        setShowSettingsPanel(true);
    };

    const queueParentHeightUpdate = () => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                notifyParentHeightRef.current();
            });
        });
    };

    const postParentImageModalMessage = (payload) => {
        window.parent.postMessage(payload, '*');
    };

    const closeParentImageModal = () => {
        postParentImageModalMessage({
            type: 'FALCON_SMILE_IMAGE_MODAL_CLOSE'
        });
    };

    const unwrapClinicAttributeValue = (value) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
        if ('S' in value) return value.S;
        if ('N' in value) return value.N;
        if ('BOOL' in value) return value.BOOL;
        if ('L' in value) return value.L.map(unwrapClinicAttributeValue);
        if ('M' in value) {
            return Object.fromEntries(
                Object.entries(value.M).map(([key, item]) => [key, unwrapClinicAttributeValue(item)])
            );
        }
        return value;
    };

    const normalizeLocationField = (value) => String(value || '').trim();

    const normalizeClinicLocationEntry = (location = {}) => ({
        name: normalizeLocationField(location.name),
        address: normalizeLocationField(location.address),
        latitude: normalizeLocationField(location.latitude ?? location.lat),
        longitude: normalizeLocationField(location.longitude ?? location.lng),
        phoneCountryCode: normalizeLocationField(location.phoneCountryCode || '+1'),
        phoneNumber: normalizeLocationField(location.phoneNumber),
        whatsappSameAsPhone: Boolean(location.whatsappSameAsPhone ?? true),
        whatsappCountryCode: normalizeLocationField(location.whatsappCountryCode || '+1'),
        whatsappNumber: normalizeLocationField(location.whatsappNumber)
    });

    const getThemeSettingsFromClinicBranding = (clinicRecord) => {
        const branding = unwrapClinicAttributeValue(clinicRecord?.branding);
        if (!branding || typeof branding !== 'object') return null;
        const domainSettings = branding.domainSettings && typeof branding.domainSettings === 'object'
            ? branding.domainSettings
            : null;
        const matchedDomainSettings = domainSettings
            ? Object.entries(domainSettings).find(([domain]) => getDomainNameFromOrigin(domain) === CURRENT_DOMAIN_NAME)?.[1]
            : null;
        if (!matchedDomainSettings || typeof matchedDomainSettings !== 'object') return null;
        const brandingSource = matchedDomainSettings;

        const nextThemeSettings = {};

        if (Array.isArray(brandingSource.clinicLocations)) {
            nextThemeSettings.clinicLocations = brandingSource.clinicLocations.map(normalizeClinicLocationEntry);
        }

        const allowedThemeKeys = Object.keys(defaultThemeSettings).filter(key => key !== 'clinicLocations');

        allowedThemeKeys.forEach((key) => {
            const value = brandingSource[key];
            if (value === null || value === undefined || value === '') return;

            if (key === 'baseTextSize') {
                const numericValue = Number(value);
                if (!Number.isNaN(numericValue)) {
                    nextThemeSettings[key] = numericValue;
                }
                return;
            }

            if (key === 'clinicInstructions') {
                nextThemeSettings[key] = String(value).trim();
                return;
            }

            if (
                key === 'showSpecificInstructions' ||
                key === 'showFixedAppointment' ||
                key === 'showLocations' ||
                key === 'showAIConcierge' ||
                key === 'showPersonalizedAssessment' ||
                key === 'showAssessmentSpecialists' ||
                key === 'showAssessmentPrice' ||
                key === 'showAssessmentTimeline' ||
                key === 'showAssessmentEstimateCards' ||
                key === 'showAIConsultation'
            ) {
                nextThemeSettings[key] = Boolean(value);
                return;
            }

            nextThemeSettings[key] = value;
        });

        if (brandingSource.showAssessmentEstimateCards !== undefined) {
            const estimateEnabled = Boolean(brandingSource.showAssessmentEstimateCards);
            if (nextThemeSettings.showAssessmentPrice === undefined) {
                nextThemeSettings.showAssessmentPrice = estimateEnabled;
            }
            if (nextThemeSettings.showAssessmentTimeline === undefined) {
                nextThemeSettings.showAssessmentTimeline = estimateEnabled;
            }
        }

        return Object.keys(nextThemeSettings).length ? nextThemeSettings : null;
    };

    const applyClinicBrandingTheme = (clinicRecord) => {
        const clinicThemeSettings = getThemeSettingsFromClinicBranding(clinicRecord);
        if (!clinicThemeSettings) return;

        setThemeSettings(prev => ({ ...prev, ...clinicThemeSettings }));
        setDraftThemeSettings(prev => ({ ...prev, ...clinicThemeSettings }));
    };

    const sendHttpRequest = async (path, options = {}, timeoutMs = 30000) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        try {
            const response = await fetch(`${HTTP_API_BASE_URL}${path}`, {
                ...options,
                signal: controller.signal,
                headers: {
                    'x-api-key': API_KEY,
                    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
                    ...(options.headers || {})
                }
            });

            const contentType = response.headers.get('content-type') || '';
            const payload = contentType.includes('application/json')
                ? await response.json()
                : await response.text();

            if (!response.ok) {
                const message = typeof payload === 'object' && payload?.message
                    ? payload.message
                    : `Request failed with status ${response.status}`;
                throw new Error(message);
            }

            return payload;
        } finally {
            clearTimeout(timeoutId);
        }
    };

    const fetchClinicProfile = async () => {
        setIsInitialLoading(true);
        try {
            const payload = await sendHttpRequest(`/clinic/${encodeURIComponent(API_KEY)}`, {
                method: 'GET',
                headers: {
                    'x-api-key': API_KEY
                }
            }, 15000);
            const clinicRecord = payload?.data || null;
            setClinicData(clinicRecord);
            applyClinicBrandingTheme(clinicRecord);
            setConnectionRetryError(null);
            setIsInitialLoading(false);
        } catch (error) {
            console.error("Clinic profile request failed", error);
            setIsInitialLoading(false);
            setConnectionRetryError({
                message: error.message || "Unable to load clinic profile. Please try again in a moment."
            });
        }
    };

    const withRequestContext = (data = {}) => ({
        ...data,
        apiKey: data.apiKey || API_KEY,
        parentOrigin: PARENT_ORIGIN,
        frameOrigin: FRAME_ORIGIN
    });

    const sendRequestThroughOpenSocket = (ws, action, response, data, timeoutMs = 30000) => {
        if (timeoutMapRef.current[response]) {
            clearRequestTimeout(response);
        }

        const timeoutId = setTimeout(() => {
            // Request timed out
            delete timeoutMapRef.current[response];

            // Clear specific loading states based on action type
            if (action === 'conversational_chat') {
                setIsChatting(false);
            } else if (action === 'confirm_appointment') {
                setIsConfirmingAppointment(false);
            } else if (action === 'transform_image' || action === 'get_signed_url') {
                setLoading(false);
                setIsRegenerating(false);
                setGenerationProgress({ step: 'idle', uploadProgress: 0 });
            }

            setRetryableError({
                action,
                response,
                data,
                timeoutMs,
                message: "Request timed out. Please check your connection and try again."
            });
        }, timeoutMs);

        timeoutMapRef.current[response] = timeoutId;

        ws.send(JSON.stringify({
            action,
            data: withRequestContext(data)
        }));
    };

    // Helper function to create a request with timeout
    const sendWebSocketRequest = (action, response, data, timeoutMs = 30000) => {
        const ws = socketRef.current;

        if (ws?.readyState === WebSocket.OPEN) {
            sendRequestThroughOpenSocket(ws, action, response, data, timeoutMs);
            return;
        }

        establishWebSocketConnectionAndHandleServerAction({ showErrorOnFailure: true })
            .then(openSocket => {
                sendRequestThroughOpenSocket(openSocket, action, response, data, timeoutMs);
            })
            .catch(() => {
                if (action === 'conversational_chat') {
                    setIsChatting(false);
                } else if (action === 'confirm_appointment') {
                    setIsConfirmingAppointment(false);
                } else if (action === 'transform_image' || action === 'get_signed_url') {
                    setLoading(false);
                    setIsRegenerating(false);
                    setGenerationProgress({ step: 'idle', uploadProgress: 0 });
                }

                setRetryableError({
                    action,
                    response,
                    data,
                    timeoutMs,
                    message: "Unable to reconnect to the clinical server. Please check your connection and try again."
                });
            });
    };

    // Helper function to clear timeout on successful response
    const clearRequestTimeout = (response) => {
        if (timeoutMapRef.current[response]) {
            clearTimeout(timeoutMapRef.current[response]);
            delete timeoutMapRef.current[response];
        }
    };

    const clearMatchingRequestTimeouts = (...responseKeys) => {
        responseKeys
            .filter(Boolean)
            .forEach(responseKey => clearRequestTimeout(responseKey));
    };

    const clearAllRequestTimeouts = () => {
        Object.keys(timeoutMapRef.current).forEach(responseKey => clearRequestTimeout(responseKey));
    };

    const establishWebSocketConnectionAndHandleServerAction = ({
        showErrorOnFailure = false
    } = {}) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            return Promise.resolve(socketRef.current);
        }

        if (socketRef.current?.readyState === WebSocket.CONNECTING && connectionPromiseRef.current) {
            return connectionPromiseRef.current;
        }

        let ws;
        const connectionPromise = new Promise((resolve, reject) => {
            try {
                ws = new WebSocket(`${WEBSOCKET_URL}?apiKeyParam=${encodeURIComponent(API_KEY)}`);
                socketRef.current = ws;
                ws.onopen = () => {
                    console.log("Connected to Clinical Gateway");
                    connectionPromiseRef.current = null;
                    setConnectionRetryError(null);
                    setError(null);
                    resolve(ws);
                };
                ws.onmessage = (event) => {
                    try {
                        const payload = JSON.parse(event.data);
                        console.log("WS payload", payload);

                        // Clear timeout if this is a response to a pending request.
                        clearMatchingRequestTimeouts(payload.action, payload.response);

                        handleServerAction(payload);
                    } catch (e) {
                        console.error("Socket error", e);
                    }
                };
                ws.onclose = (event) => {
                    console.log("WebSocket closed", {
                        code: event.code,
                        reason: event.reason,
                        wasClean: event.wasClean,
                    });

                    if (socketRef.current === ws) {
                        socketRef.current = null;
                    }
                    connectionPromiseRef.current = null;

                    // Clear all pending timeouts since this connection can no longer answer them.
                    Object.keys(timeoutMapRef.current).forEach(response => {
                        clearRequestTimeout(response);
                    });

                    setIsInitialLoading(false);
                    const message = event.wasClean
                        ? "The connection was closed before the plugin finished loading."
                        : `Connection closed unexpectedly: ${event.code} ${event.reason || ""}`;
                    if (showErrorOnFailure) {
                        setConnectionRetryError({ message });
                    }
                    reject(new Error(message));
                };
                ws.onerror = (event) => {
                    console.error("WebSocket error", event);
                    connectionPromiseRef.current = null;
                    // Clear all pending timeouts since connection failed
                    Object.keys(timeoutMapRef.current).forEach(response => {
                        clearRequestTimeout(response);
                    });
                    resetTransformationState();
                    setIsInitialLoading(false);
                    const message = `Unable to connect to the clinical server. The iframe is running from ${FRAME_ORIGIN} and was embedded by ${PARENT_ORIGIN}. Please verify the origin configuration and retry the connection.`;
                    if (showErrorOnFailure) {
                        setConnectionRetryError({ message });
                    }
                    reject(new Error(message));
                };
            } catch (error) {
                console.error("Error establishing WebSocket connection", error);
                connectionPromiseRef.current = null;
                setIsInitialLoading(false);
                const message = "Unable to start the clinical server connection. Please retry.";
                if (showErrorOnFailure) {
                    setConnectionRetryError({ message });
                }
                reject(new Error(message));
            }
        });

        connectionPromiseRef.current = connectionPromise;
        return connectionPromise;
    }

    // --- INITIAL CLINIC PROFILE LOAD ---
    useEffect(() => {
        // Copy ref to variable to avoid closure issues in cleanup
        const timeoutMap = timeoutMapRef.current;

        fetchClinicProfile();

        let lastHeight = 0;
        let frameId = null;

        const getHeight = () => {
            if (!iframeContentRef.current) return 0;
            return Math.ceil(iframeContentRef.current.getBoundingClientRect().height);
        };

        const sendHeight = () => {
            if (frameId) cancelAnimationFrame(frameId);

            frameId = requestAnimationFrame(() => {
                const height = getHeight();
                if (!height || height === lastHeight) return;

                lastHeight = height;

                window.parent.postMessage(
                    {
                        type: 'FALCON_SMILE_HEIGHT',
                        height
                    },
                    '*'
                );
            });
        };

        notifyParentHeightRef.current = sendHeight;
        sendHeight();

        const resizeObserver = new ResizeObserver(sendHeight);
        if (iframeContentRef.current) {
            resizeObserver.observe(iframeContentRef.current);
        }

        window.addEventListener('load', sendHeight);
        window.addEventListener('resize', sendHeight);

        // Cleanup function to clear all pending timeouts on unmount
        return () => {
            notifyParentHeightRef.current = () => { };
            if (frameId) cancelAnimationFrame(frameId);
            resizeObserver.disconnect();
            window.removeEventListener('load', sendHeight);
            window.removeEventListener('resize', sendHeight);
            Object.values(timeoutMap).forEach(timeoutId => clearTimeout(timeoutId));
            if (smileSessionTimeoutRef.current) clearTimeout(smileSessionTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatMessages]);

    useEffect(() => {
        specificInstructionsRef.current = specificInstructions;
        draftThemeSettingsRef.current = draftThemeSettings;
        themeSettingsRef.current = themeSettings;
        userDataRef.current = userData;
        patientDataRef.current = patientData;
    }, [specificInstructions, draftThemeSettings, themeSettings, userData, patientData]);

    useEffect(() => {
        const handleParentImageModalAction = (event) => {
            const { data } = event;
            if (!data || data.type !== 'FALCON_SMILE_IMAGE_MODAL_ACTION') return;

            if (data.action === 'primary' && pendingImageAction) {
                proceedWithGuidedAction();
                return;
            }

            if (data.action === 'close' || data.action === 'secondary') {
                closeGuide();
            }
        };

        window.addEventListener('message', handleParentImageModalAction);

        return () => {
            window.removeEventListener('message', handleParentImageModalAction);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingImageAction]);

    useEffect(() => {
        if (!showAppointmentForm || bookingFlow === 'confirmed') return;

        bookingSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    }, [showAppointmentForm, bookingFlow]);

    useEffect(() => {
        if (!pendingImageAction) return;

        scrollToTopOfApp('smooth');
        guideModalRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    }, [pendingImageAction]);

    useEffect(() => {
        const durationSeconds = TRANSFORM_DURATION_SECONDS;

        if (generationProgress.step !== 'transform') {
            setTransformTimeLeft(durationSeconds);
            return undefined;
        }

        setTransformTimeLeft(durationSeconds);
        const intervalId = setInterval(() => {
            setTransformTimeLeft(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => clearInterval(intervalId);
    }, [generationProgress.step]);

    const resetTransformationState = () => {
        setLoading(false);
        setIsRegenerating(false);
        setGenerationProgress({ step: 'idle', uploadProgress: 0 });
        pendingResizedBlobRef.current = null;
        uploadedImageUrlRef.current = '';
        patientDataRef.current = null;
        setPatientData(null);
    };

    const clearSmileSessionTimer = () => {
        if (smileSessionTimeoutRef.current) {
            clearTimeout(smileSessionTimeoutRef.current);
            smileSessionTimeoutRef.current = null;
        }
    };

    const startSmileSessionTimer = () => {
        clearSmileSessionTimer();
        smileSessionTimeoutRef.current = setTimeout(() => {
            smileSessionTimeoutRef.current = null;
            stopCameraStream();
            resetPluginToInitialUploadStage('Your smile preview session expired after 30 minutes. Please start again.');
        }, SMILE_SESSION_TIMEOUT_MS);
    };

    const resetPluginToInitialUploadStage = (message = null) => {
        clearSmileSessionTimer();
        clearAllRequestTimeouts();
        resetTransformationState();
        scrollToTopOfApp('auto');
        setImage(null);
        setMaskCoordinates(null);
        setVisualOverlayBase64Image(null);
        setSpecificInstructions("");
        setResults([]);
        setTriageData(null);
        setEstimates(null);
        setPatientData(null);
        setRetryableError(null);
        setConsentChecked(false);
        setConsentFormError('');
        setPendingImageAction(null);
        setShowStockImagePicker(false);
        setSelectedStockImageUrl('');
        setViewerImage(null);
        setSelectedSmileImageKey('original');
        setUserInput("");
        setIsChatting(false);
        setIsConfirmingAppointment(false);
        setIsSavingTheme(false);
        setBookingFlow('idle');
        setUserData(initialUserData);
        setPreferredSlots(getDefaultPreferredSlots(getClinicOptions(clinicData?.days), APPOINTMENT_TIME_INTERVALS));
        setChatMessages(initialChatMessages);
        pendingAppointmentSlotsRef.current = [];
        pendingTransformationResultsRef.current = [];
        pendingChatTextRef.current = "";
        pendingChatRequestRef.current = null;
        chatRetryCountRef.current = 0;
        if (chatRetryTimeoutRef.current) {
            clearTimeout(chatRetryTimeoutRef.current);
            chatRetryTimeoutRef.current = null;
        }
        setConnectionRetryError(null);
        setError(message);
        queueParentHeightUpdate();
    };

    const cleanAppointmentUpdateData = (updateData = {}) => {
        return Object.fromEntries(
            Object.entries(updateData).filter(([, value]) => {
                if (value === null || value === undefined) return false;
                if (typeof value === 'string' && !value.trim()) return false;
                return true;
            })
        );
    };

    const parseJsonLikeAiReply = (text) => {
        if (typeof text !== 'string') return null;

        const trimmedText = text.trim();
        const withoutFence = trimmedText
            .replace(/^```(?:json)?\s*/i, '')
            .replace(/\s*```$/i, '')
            .trim();

        const jsonStart = withoutFence.indexOf('{');
        const jsonEnd = withoutFence.lastIndexOf('}');

        if (jsonStart === -1 || jsonEnd === -1 || jsonEnd <= jsonStart) return null;

        try {
            return JSON.parse(withoutFence.slice(jsonStart, jsonEnd + 1));
        } catch {
            return null;
        }
    };

    const normalizeChatLinkValue = (value) => {
        return typeof value === 'string' && value.trim() ? value.trim() : '';
    };

    const stripQueryFromUrl = (value) => {
        const rawValue = String(value || '').trim();
        if (!rawValue) return '';
        if (!/^https?:\/\//i.test(rawValue)) return rawValue;
        try {
            const url = new URL(rawValue);
            url.search = '';
            url.hash = '';
            return url.href;
        } catch {
            return rawValue.split('?')[0].split('#')[0];
        }
    };

    const normalizeChatReplyPayload = (payload) => {
        const parsedReply = parseJsonLikeAiReply(payload.reply);
        const parsedReplyObject = parsedReply && typeof parsedReply === 'object' ? parsedReply : null;

        if (!parsedReplyObject) {
            return {
                ...payload,
                sourceLink: normalizeChatLinkValue(payload.sourceLink),
                linkText: normalizeChatLinkValue(payload.linkText)
            };
        }

        return {
            ...payload,
            reply: normalizeChatLinkValue(parsedReplyObject.reply) || payload.reply,
            sourceLink: normalizeChatLinkValue(parsedReplyObject.sourceLink) || normalizeChatLinkValue(payload.sourceLink),
            linkText: normalizeChatLinkValue(parsedReplyObject.linkText) || normalizeChatLinkValue(payload.linkText),
            updateData: {
                ...(payload.updateData || {}),
                ...(parsedReplyObject.updateData || {})
            }
        };
    };

    const retryLastChatRequest = (message) => {
        clearRequestTimeout('chat_reply');

        if (!pendingChatRequestRef.current || chatRetryCountRef.current >= 2) {
            setIsChatting(false);
            pendingChatRequestRef.current = null;
            chatRetryCountRef.current = 0;
            setChatMessages(prev => [...prev, {
                role: 'bot',
                text: message || "The AI is currently busy. Please try your question again in a moment."
            }]);
            return;
        }

        chatRetryCountRef.current += 1;
        setIsChatting(true);

        if (chatRetryTimeoutRef.current) {
            clearTimeout(chatRetryTimeoutRef.current);
        }

        chatRetryTimeoutRef.current = setTimeout(() => {
            chatRetryTimeoutRef.current = null;
            sendWebSocketRequest(
                'conversational_chat',
                'chat_reply',
                pendingChatRequestRef.current,
                30000
            );
        }, 200);
    };

    const handleServerAction = (payload) => {
        switch (payload.action) {
            case 'signed_url_response':
                // STEP 2: We received the presigned URL, now upload to S3 and trigger transform
                handlePresignedUrlReceived(payload.url);
                break;
            case 'transformation_complete':
                {
                    const returnedPatientData = payload.patientData || payload.patient_data || null;
                    patientDataRef.current = returnedPatientData;
                    setPatientData(returnedPatientData);
                }
                setResults((payload.transformedImages || []).map(stripQueryFromUrl));
                setTriageData(payload.triage || null);
                setEstimates(payload.estimates || null);
                setGenerationProgress({ step: 'finalize', uploadProgress: 100 });
                setLoading(false);
                setIsRegenerating(false);
                if (typeof onAnalysisReady === 'function') {
                    onAnalysisReady({
                        originalImageUrl: uploadedImageUrlRef.current || '',
                        originalImage: image,
                        alternatives: (payload.transformedImages || []).map(stripQueryFromUrl),
                        triage: payload.triage || null
                    });
                }
                break;
            case 'chat_reply':
                {
                    const chatPayload = normalizeChatReplyPayload(payload);
                    const cleanUpdateData = cleanAppointmentUpdateData(chatPayload.updateData);
                    const nextUserData = {
                        ...userData,
                        ...cleanUpdateData
                    };
                    setUserData(nextUserData);
                    pendingChatTextRef.current = "";
                    pendingChatRequestRef.current = null;
                    chatRetryCountRef.current = 0;

                    setChatMessages(prev => [...prev, {
                        role: 'bot',
                        text: chatPayload.reply,
                        sourceLink: chatPayload.sourceLink,
                        linkText: chatPayload.linkText
                    }]);
                }
                setIsChatting(false); // <--- DEACTIVATE LOADER
                break;
            case 'chat_error':
                retryLastChatRequest(payload.message);
                break;
            case 'appointment_confirmed':
                setIsConfirmingAppointment(false);
                handleFinalBooking();
                break;
            case 'server_error':
                if (payload.response && timeoutMapRef.current[payload.response]) clearRequestTimeout(payload.response);
                setRetryableError(null);
                setIsChatting(false);
                setIsConfirmingAppointment(false);
                setError(payload.message || "Transformation failed. Please try again.");
                resetTransformationState();
                break;
            default:
                console.warn("Unknown action from server:", payload.action);
        }
    };

    const uploadImageToS3WithProgress = (presignedUrl, imageBlob) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.onprogress = (event) => {
                if (!event.lengthComputable) return;
                const uploadProgress = Math.round((event.loaded / event.total) * 100);
                setGenerationProgress({ step: 'upload', uploadProgress });
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    setGenerationProgress({ step: 'upload', uploadProgress: 100 });
                    resolve();
                    return;
                }

                reject(new Error(`S3 upload failed with status ${xhr.status}`));
            };

            xhr.onerror = () => reject(new Error("S3 upload failed."));
            xhr.ontimeout = () => reject(new Error("S3 upload timed out."));
            xhr.open('PUT', presignedUrl);
            xhr.setRequestHeader('Content-Type', 'image/jpeg');
            xhr.timeout = 30000;
            xhr.send(imageBlob);
        });
    };

    const handlePresignedUrlReceived = async (presignedUrl) => {
        if (!pendingResizedBlobRef.current) {
            setError("Image data lost. Please try again.");
            setLoading(false);
            setIsRegenerating(false);
            setGenerationProgress({ step: 'idle', uploadProgress: 0 });
            return;
        }

        try {
            // STEP 2: Upload image to S3
            setGenerationProgress({ step: 'upload', uploadProgress: 0 });
            await uploadImageToS3WithProgress(presignedUrl, pendingResizedBlobRef.current);

            // Extract the S3 URL from the presigned URL (remove query parameters)
            const s3Url = presignedUrl.split('?')[0];
            uploadedImageUrlRef.current = s3Url;

            const clinicInstructions = String((draftThemeSettingsRef.current.clinicInstructions || themeSettingsRef.current.clinicInstructions) || '').trim();
            const showSpecificInstructions = Boolean(draftThemeSettingsRef.current.showSpecificInstructions ?? themeSettingsRef.current.showSpecificInstructions);
            const userInstructions = showSpecificInstructions ? specificInstructionsRef.current.trim() : '';
            const userPrompt = [clinicInstructions, userInstructions].filter(Boolean).join('\n\n');
            const age = Number(userDataRef.current.age) || null;
            const patientData = {
                name: String(userDataRef.current.name || '').trim(),
                email: String(userDataRef.current.contact || '').trim(),
                phone: String(userDataRef.current.phone || '').trim(),
                age,
                dob: getDobFromAge(age),
                gender: String(userDataRef.current.gender || '').trim()
            };

            // STEP 3: Send transform_image action with S3 URL
            setGenerationProgress({ step: 'transform', uploadProgress: 100 });
            sendWebSocketRequest(
                'transform_image',
                'transformation_complete',
                {
                    apiKey: API_KEY,
                    url: s3Url,
                    type: DEFAULT_TRANSFORM_TYPE,
                    userPrompt,
                    clinicInstructions,
                    userInstructions,
                    patientData,
                    ovalBounds: maskCoordinates
                },
                10 * 320000
            );

            // Cleanup
            pendingResizedBlobRef.current = null;
        } catch (err) {
            setError("Failed to upload image to S3. Please try again.");
            resetTransformationState();
            console.error(err);
        }
    };

    const getVideoInputDevices = async () => {
        if (!navigator.mediaDevices?.enumerateDevices) return [];
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            console.log('[FalconSmile camera] video input devices', videoDevices.map((device, index) => ({
                index,
                deviceId: device.deviceId,
                groupId: device.groupId,
                label: device.label
            })));
            return videoDevices;
        } catch {
            console.log('[FalconSmile camera] enumerateDevices failed');
            return [];
        }
    };

    const stopCameraStream = () => {
        if (videoRef.current?.srcObject) {
            console.log('[FalconSmile camera] stopping active stream');
            videoRef.current.srcObject.getTracks().forEach(track => track.stop());
            videoRef.current.srcObject = null;
        }
    };

    const getCameraErrorMessage = (err) => {
        const errorName = err?.name || '';
        if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError' || errorName === 'SecurityError') {
            return 'Camera access denied. Please allow camera access in your browser settings.';
        }
        if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
            return 'No camera was found on this device.';
        }
        if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
            return 'The camera is already in use by another app.';
        }
        return 'We could not open the camera. Please try again.';
    };

    const requestCameraStream = async ({ facingMode = 'user', deviceId = '', allowGenericFallback = true } = {}) => {
        if (!navigator.mediaDevices?.getUserMedia) {
            throw new Error('Camera is not supported in this browser.');
        }

        const constraintAttempts = deviceId
            ? [
                { video: { deviceId: { exact: deviceId } } },
                { video: { deviceId: { ideal: deviceId } } },
                { video: { facingMode: { exact: facingMode } } },
                { video: { facingMode: { ideal: facingMode } } },
                ...(allowGenericFallback ? [{ video: true }] : [])
            ]
            : [
                { video: { facingMode: { exact: facingMode } } },
                { video: { facingMode: { ideal: facingMode } } },
                ...(allowGenericFallback ? [{ video: true }] : [])
            ];

        let lastError = null;
        for (const constraints of constraintAttempts) {
            try {
                console.log('[FalconSmile camera] requesting stream', constraints);
                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                const track = stream.getVideoTracks()[0];
                console.log('[FalconSmile camera] stream opened', track?.getSettings?.() || {});
                return stream;
            } catch (err) {
                lastError = err;
                console.log('[FalconSmile camera] stream request failed', {
                    constraints,
                    name: err?.name,
                    message: err?.message
                });
                if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError' || err?.name === 'SecurityError') {
                    throw err;
                }
            }
        }

        throw lastError || new Error('Camera unavailable.');
    };

    //FE
    const startCamera = async ({ facingMode = 'user', deviceId = '', allowGenericFallback = true, stopBeforeRequest = false } = {}) => {
        setIsCapturing(true);
        setError(null);

        try {
            console.log('[FalconSmile camera] startCamera', {
                facingMode,
                deviceId,
                allowGenericFallback,
                stopBeforeRequest
            });
            if (stopBeforeRequest) {
                stopCameraStream();
            }
            const stream = await requestCameraStream({ facingMode, deviceId, allowGenericFallback });
            if (!stopBeforeRequest) {
                stopCameraStream();
            }
            if (videoRef.current) videoRef.current.srcObject = stream;
            const activeTrack = stream.getVideoTracks()[0];
            const activeSettings = activeTrack?.getSettings?.() || {};
            if (!allowGenericFallback && activeSettings.facingMode && activeSettings.facingMode !== facingMode) {
                console.log('[FalconSmile camera] opened wrong facing mode, rejecting stream', {
                    requested: facingMode,
                    received: activeSettings.facingMode
                });
                stream.getTracks().forEach(track => track.stop());
                throw new Error(`Requested ${facingMode} camera but opened ${activeSettings.facingMode}.`);
            }
            setActiveCameraDeviceId(activeSettings.deviceId || deviceId || '');
            setCameraFacingMode(activeSettings.facingMode || facingMode);
            const nextCameraDevices = await getVideoInputDevices();
            setCameraDevices(nextCameraDevices);
            console.log('[FalconSmile camera] active camera saved', {
                activeDeviceId: activeSettings.deviceId || deviceId || '',
                activeFacingMode: activeSettings.facingMode || facingMode,
                deviceCount: nextCameraDevices.length
            });
            return true;
        } catch (err) {
            console.log('[FalconSmile camera] startCamera failed', {
                name: err?.name,
                message: err?.message
            });
            setError(getCameraErrorMessage(err));
            setIsCapturing(false);
            return false;
        }
    };

    const findCameraDeviceByFacing = (devices, facingMode) => {
        const targetPattern = facingMode === 'environment'
            ? /(back|rear|environment|world|facing back)/i
            : /(front|user|face|facing front)/i;
        return devices.find(device => targetPattern.test(device.label || ''));
    };

    const switchCamera = async () => {
        if (isSwitchingCamera || cameraDevices.length < 2) return;

        setIsSwitchingCamera(true);
        try {
            try {
                const latestCameraDevices = await getVideoInputDevices();
                const availableCameraDevices = latestCameraDevices.length ? latestCameraDevices : cameraDevices;
                setCameraDevices(availableCameraDevices);
                const nextFacingMode = cameraFacingMode === 'environment' ? 'user' : 'environment';
                const matchingDevice = findCameraDeviceByFacing(availableCameraDevices, nextFacingMode);
                console.log('[FalconSmile camera] switchCamera', {
                    activeCameraDeviceId,
                    cameraFacingMode,
                    nextFacingMode,
                    matchingDevice: matchingDevice ? {
                        deviceId: matchingDevice.deviceId,
                        label: matchingDevice.label
                    } : null,
                    availableCameraDevices: availableCameraDevices.map((device, index) => ({
                        index,
                        deviceId: device.deviceId,
                        label: device.label
                    }))
                });

                const didFacingModeSwitch = await startCamera({
                    facingMode: nextFacingMode,
                    allowGenericFallback: false,
                    stopBeforeRequest: true
                });
                if (didFacingModeSwitch) return;

                if (matchingDevice?.deviceId && matchingDevice.deviceId !== activeCameraDeviceId) {
                    const didSwitch = await startCamera({
                        deviceId: matchingDevice.deviceId,
                        facingMode: nextFacingMode,
                        allowGenericFallback: false,
                        stopBeforeRequest: true
                    });
                    if (didSwitch) return;
                }

                const currentIndex = availableCameraDevices.findIndex(device => device.deviceId === activeCameraDeviceId);
                const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % availableCameraDevices.length : 1;
                const nextDeviceId = availableCameraDevices[nextIndex]?.deviceId;

                if (nextDeviceId) {
                    const didSwitch = await startCamera({
                        deviceId: nextDeviceId,
                        facingMode: nextFacingMode,
                        allowGenericFallback: false,
                        stopBeforeRequest: true
                    });
                    if (didSwitch) return;
                }

                const didSwitch = await startCamera({
                    facingMode: nextFacingMode,
                    allowGenericFallback: false,
                    stopBeforeRequest: true
                });
                if (didSwitch) return;
                await startCamera({ facingMode: cameraFacingMode || 'user' });
            } catch {
                await startCamera({ facingMode: cameraFacingMode || 'user' });
            }
        } finally {
            setIsSwitchingCamera(false);
        }
    };

    // const capturePhoto = () => {
    //     const canvas = document.createElement('canvas');
    //     canvas.width = videoRef.current.videoWidth; 
    // canvas.height = videoRef.current.videoHeight;
    //     canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    //     canvas.toBlob((blob) => {
    //         const file = new File([blob], "capture.jpg", { type: "image/jpeg" });
    //         processImage(file);
    //     }, 'image/jpeg', 0.95);
    //     stopCamera();
    // };


    const capturePhoto = () => {
        const videoWidth = videoRef.current.videoWidth;
        const videoHeight = videoRef.current.videoHeight;

        // --- CANVAS 1: THE ORIGINAL PHOTOGRAPH ---
        const canvas = document.createElement('canvas');
        canvas.width = videoWidth;
        canvas.height = videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoRef.current, 0, 0);

        // --- STEP 1: CALCULATE THE RAW COORDINATES ONCE ---
        const cx = videoWidth / 2;
        const cy = videoHeight / 2;
        const baseScale = Math.min(videoWidth, videoHeight);
        const rx = (baseScale * 0.55) / 2;
        const ry = (baseScale * 0.85) / 2;

        // --- STEP 2: SAVE THE MULTIPLIER PERCENTAGES FOR THE BACKEND ---
        const calculatedBounds = {
            cxPercent: cx / videoWidth,
            cyPercent: cy / videoHeight,
            rxPercent: rx / videoWidth,
            ryPercent: ry / videoHeight
        };
        setMaskCoordinates(calculatedBounds);

        // --- STEP 3: RECONSTRUCT ABSOLUTE PIXELS FROM THE BOUNDS OBJECT ---
        // This explicitly proves that your percent-scaling logic works flawlessly!
        const absoluteCx = videoWidth * calculatedBounds.cxPercent;
        const absoluteCy = videoHeight * calculatedBounds.cyPercent;
        const absoluteRx = videoWidth * calculatedBounds.rxPercent;
        const absoluteRy = videoHeight * calculatedBounds.ryPercent;

        // --- STEP 4: GENERATE THE VISUAL PREVIEW OVERLAY ---
        const debugCanvas = document.createElement('canvas');
        debugCanvas.width = videoWidth;
        debugCanvas.height = videoHeight;
        const debugCtx = debugCanvas.getContext('2d');

        // Draw base photograph
        debugCtx.drawImage(canvas, 0, 0);

        // Composite the semi-transparent black framing screen
        debugCtx.fillStyle = 'rgba(0, 0, 0, 0.4)';
        debugCtx.fillRect(0, 0, videoWidth, videoHeight);

        // Clear out the focus window using the structural object values
        debugCtx.save();
        debugCtx.beginPath();
        debugCtx.ellipse(absoluteCx, absoluteCy, absoluteRx, absoluteRy, 0, 0, 2 * Math.PI);
        debugCtx.clip();
        debugCtx.drawImage(canvas, 0, 0);
        debugCtx.restore();

        // Paint the clean white contour ring using the identical metrics
        debugCtx.beginPath();
        debugCtx.ellipse(absoluteCx, absoluteCy, absoluteRx, absoluteRy, 0, 0, 2 * Math.PI);
        debugCtx.strokeStyle = '#FFFFFF';
        debugCtx.lineWidth = 6;
        debugCtx.stroke();

        // --- STEP 5: SAVE ASSETS TO STATE ---
        const originalBase64 = canvas.toDataURL('image/jpeg', 0.95);
        const UIOverlayVisualBase64 = debugCanvas.toDataURL('image/jpeg', 0.95);

        setImage(originalBase64);
        setVisualOverlayBase64Image(UIOverlayVisualBase64);

        setSpecificInstructions("");
        setResults([]);
        setTriageData(null);
        setEstimates(null);
        patientDataRef.current = null;
        setPatientData(null);
        setUserData(prev => ({ ...prev, gender: 'not_say' }));
        setError(null);
        queueParentHeightUpdate();

        stopCamera();
    };

    const stopCamera = () => {
        stopCameraStream();
        setIsCapturing(false);
        setActiveCameraDeviceId('');
    };

    const cancelCameraCapture = () => {
        stopCamera();
        clearSmileSessionTimer();
    };

    const processImage = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            // We set the HD image directly to state for the UI to display
            setImage(e.target.result);
            setMaskCoordinates(null);
            setVisualOverlayBase64Image(null);
            setSpecificInstructions("");
            setResults([]);
            setTriageData(null);
            setEstimates(null);
            patientDataRef.current = null;
            setPatientData(null);
            setUserData(prev => ({ ...prev, gender: 'not_say' }));
            setError(null);
            queueParentHeightUpdate();
        };
        reader.readAsDataURL(file);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) processImage(file);
    };

    const openGuideForAction = (action) => {
        if (action === 'camera' || action === 'upload') {
            startSmileSessionTimer();
        }
        setPendingImageAction(action);
        setConsentChecked(false);
        setConsentFormError('');
        setSpecificInstructions("");
        scrollToTopOfApp('auto');
        // openParentImageModal({
        //     variant: 'guide',
        //     title: 'Follow this guide before continuing',
        //     subtitle: 'Photo Guide',
        //     imageSrc: new URL(guideImage, window.location.href).href,
        //     imageAlt: 'DentalLive photo guide',
        //     primaryLabel: 'Proceed',
        //     secondaryLabel: 'Cancel'
        // });
    };

    const closeGuide = () => {
        clearSmileSessionTimer();
        setPendingImageAction(null);
        setConsentFormError('');
        closeParentImageModal();
    };

    const validatePhotoGuideConsent = () => {
        const trimmedName = String(userData.name || '').trim();
        const trimmedContact = String(userData.contact || '').trim();
        const trimmedPhone = String(userData.phone || '').trim();

        if (!trimmedName) {
            return 'Please enter your full name before continuing.';
        }

        if (!isValidAppointmentName(trimmedName)) {
            return 'Please enter a proper name.';
        }

        if (!trimmedContact) {
            return 'Please enter your email address before continuing.';
        }

        if (!isValidAppointmentEmail(trimmedContact)) {
            return 'Please enter a proper email address.';
        }

        if (trimmedPhone && !isValidAppointmentPhone(trimmedPhone)) {
            return 'Please enter a valid phone number.';
        }

        if (!consentChecked) {
            return 'Please confirm the disclaimer and terms before continuing.';
        }

        return '';
    };

    const proceedWithGuidedAction = () => {
        const validationMessage = validatePhotoGuideConsent();
        if (validationMessage) {
            setConsentFormError(validationMessage);
            return;
        }

        const action = pendingImageAction;
        setPendingImageAction(null);
        setConsentFormError('');
        const nextUserData = {
            ...userData,
            name: String(userData.name || '').trim(),
            contact: String(userData.contact || '').trim(),
            phone: String(userData.phone || '').trim()
        };
        userDataRef.current = nextUserData;
        setUserData(nextUserData);
        closeParentImageModal();

        if (action === 'camera') {
            startCamera();
            return;
        }

        if (action === 'upload') {
            fileInputRef.current?.click();
            return;
        }

        if (action === 'stock') {
            openStockImagePicker();
        }
    };

    const openSmileComparisonViewer = ({ images = [], initialKey = 'original' }) => {
        const normalizedImages = images
            .map((item) => ({
                key: item.key,
                label: item.label,
                src: item.src
            }))
            .filter((item) => Boolean(item.src));

        if (!normalizedImages.length) return;

        const initialImage = normalizedImages.find((item) => item.key === initialKey) || normalizedImages[0];
        const secondImage = normalizedImages.find((item) => item.key !== initialImage.key) || normalizedImages[0];
        const thirdImage = normalizedImages.find((item) => item.key !== initialImage.key && item.key !== secondImage.key)
            || normalizedImages[0];

        setViewerImage({
            mode: 'single',
            images: normalizedImages,
            singleKey: initialImage.key,
            leftKey: initialImage.key,
            rightKey: secondImage.key,
            thirdKey: thirdImage.key
        });
    };

    const closeSmileComparisonViewer = () => setViewerImage(null);

    const updateViewerSlot = (slot, key) => {
        if (!viewerImage) return;
        const nextImage = viewerImage.images.find((item) => item.key === key);
        if (!nextImage) return;
        setViewerImage((current) => {
            if (!current) return current;
            const nextState = { ...current, [`${slot}Key`]: key };
            if (slot === 'single') {
                nextState.leftKey = key;
                nextState.rightKey = current.rightKey || current.images.find((item) => item.key !== key)?.key || key;
                nextState.thirdKey = current.images.find((item) => item.key !== key && item.key !== nextState.rightKey)?.key || key;
            }
            return nextState;
        });
    };

    const setViewerMode = (mode) => {
        if (!viewerImage) return;
        setViewerImage((current) => {
            if (!current) return current;
            const fallbackFirst = current.images[0]?.key || 'original';
            const fallbackSecond = current.images.find((item) => item.key !== fallbackFirst)?.key || fallbackFirst;
            const fallbackThird = current.images.find((item) => item.key !== fallbackFirst && item.key !== fallbackSecond)?.key || fallbackFirst;
            const selectedSingle = current.singleKey || fallbackFirst;
            return {
                ...current,
                mode,
                singleKey: selectedSingle,
                leftKey: current.leftKey || selectedSingle || fallbackFirst,
                rightKey: current.rightKey || fallbackSecond,
                thirdKey: current.thirdKey || fallbackThird
            };
        });
    };

    const getViewerItem = (key) => viewerImage?.images?.find((item) => item.key === key) || null;

    const renderViewerPicker = (slotTitle, selectedKey, onChange) => (
        <div className="rounded-[1rem] sm:rounded-[1.4rem] border border-white/10 bg-white/90 p-3 sm:p-4 text-slate-900 shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
            <p className="text-[11px] sm:text-sm font-black uppercase tracking-[0.16em] sm:tracking-[0.22em] text-slate-500">{slotTitle}</p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {viewerImage?.images?.map((item) => (
                    <label
                        key={`${slotTitle}-${item.key}`}
                        className={`flex min-w-0 cursor-pointer items-center justify-center gap-1.5 rounded-[999px] border px-2.5 py-2 transition ${selectedKey === item.key
                            ? 'border-cyan-400 bg-cyan-50'
                            : 'border-slate-200 bg-white hover:border-cyan-200 hover:bg-slate-50'
                            }`}
                    >
                        <input
                            type="radio"
                            name={slotTitle}
                            value={item.key}
                            checked={selectedKey === item.key}
                            onChange={() => onChange(item.key)}
                            className="h-3.5 w-3.5 shrink-0 accent-cyan-500"
                        />
                        <span className="min-w-0 text-center text-[12px] sm:text-sm font-semibold leading-tight text-slate-800">{item.label}</span>
                    </label>
                ))}
            </div>
        </div>
    );

    const renderViewerImagePanel = (selectedKey, minHeightClass = 'min-h-[360px]', maxHeightClass = 'max-h-[65vh]') => {
        const selectedImage = getViewerItem(selectedKey);
        return (
            <div className={`flex ${minHeightClass} items-center justify-center rounded-[1.5rem] border border-white/10 bg-black/25 p-4`}>
                {selectedImage ? (
                    <img
                        src={selectedImage.src}
                        alt={selectedImage.label}
                        className={`${maxHeightClass} max-w-full object-contain`}
                    />
                ) : (
                    <div className="text-sm font-semibold text-slate-300">No image available.</div>
                )}
            </div>
        );
    };

    const getFirstAvailableImageKey = (images = []) => images.find((item) => item.src)?.key || images[0]?.key || '';

    const getSelectedCarouselImage = (images = [], selectedKey) => (
        images.find((item) => item.key === selectedKey && item.src) || images.find((item) => item.src) || null
    );

    const stepSmileCarousel = (images = [], direction) => {
        const availableImages = images.filter((item) => item.src);
        if (!availableImages.length) return;
        const selectedKey = selectedSmileImageKey || getFirstAvailableImageKey(availableImages);
        const currentIndex = Math.max(0, availableImages.findIndex((item) => item.key === selectedKey));
        const nextIndex = (currentIndex + direction + availableImages.length) % availableImages.length;
        setSelectedSmileImageKey(availableImages[nextIndex].key);
    };

    const startImageTransform = async (regenerate = false) => {
        if (!image) return;

        scrollToTopOfApp('auto');

        if (regenerate) setIsRegenerating(true);
        else setLoading(true);
        setError(null);
        patientDataRef.current = null;
        setPatientData(null);
        setGenerationProgress({ step: 'prepare', uploadProgress: 0 });

        try {
            // Load image once upfront and test dimensions
            const img = new Image();
            const imageLoaded = new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
            });
            img.src = image; // image is already a data URL
            await imageLoaded;

            // Check dimensions and resize if needed - all in one pass
            const MAX_SIZE = 1024;
            let width = img.width;
            let height = img.height;
            let resizedBlob;

            if (width > MAX_SIZE || height > MAX_SIZE) {
                console.log("Image is large, resizing...");
                if (width > height) {
                    height *= MAX_SIZE / width;
                    width = MAX_SIZE;
                } else {
                    width *= MAX_SIZE / height;
                    height = MAX_SIZE;
                }

                // Create canvas and draw image only when resizing
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                canvas.getContext('2d').drawImage(img, 0, 0, width, height);

                // Convert resized canvas to blob
                resizedBlob = await new Promise((resolve, reject) => {
                    canvas.toBlob(
                        (blob) => {
                            if (blob) resolve(blob);
                            else reject(new Error("Failed to convert canvas to blob"));
                        },
                        'image/jpeg',
                        0.8
                    );
                });
            } else {
                // Image is already small enough, convert original directly to blob
                console.log("Image is already optimized, using as-is...");
                const response = await fetch(image);
                resizedBlob = await response.blob();
            }

            // Store the resized blob for when presigned URL arrives
            pendingResizedBlobRef.current = resizedBlob;
            // Request simulation from backend once the image is ready
            setGenerationProgress({ step: 'url', uploadProgress: 0 });
            sendWebSocketRequest('get_signed_url', 'signed_url_response', { apiKey: API_KEY }, 30000);
        } catch (err) {
            setError("Simulation failed. Please try again.");
            resetTransformationState();
            console.error(err);
        }
    }

    //AI ASSISTANT FUNCTION
    const sendMessage = async () => {
        if (!userInput.trim()) return;
        const userText = userInput.trim();

        // 1. Snapshot the current history for the API (Gemini expects 'user' and 'model' roles)
        const chatHistory = chatMessages.map(msg => ({
            role: msg.role === 'user' ? 'user' : 'model',
            parts: [{ text: msg.text }]
        }));

        // 2. UI Updates
        setChatMessages(prev => [...prev, { role: 'user', text: userText }]);
        setUserInput("");
        setIsChatting(true); // <--- ACTIVATE LOADER
        pendingChatTextRef.current = userText;

        const chatRequestData = {
            apiKey: API_KEY,
            patientId: patientDataRef.current?.patientId || patientDataRef.current?.patient_id || null,
            patientData: patientDataRef.current || null,
            userText: userText,
            chatHistory: chatHistory,
            triage: triageData,
            userData: userData
        };

        pendingChatRequestRef.current = chatRequestData;
        chatRetryCountRef.current = 0;

        sendWebSocketRequest('conversational_chat', 'chat_reply', chatRequestData, 30000);
    };

    const getPreferredSlotLabels = () => {
        return preferredSlots.map(slot => `${slot.day} at ${slot.time}`);
    };

    function getConfiguredClinicLocations() {
        if (!Array.isArray(currentThemeSettings.clinicLocations) || !currentThemeSettings.clinicLocations.length) {
            return [];
        }

        return currentThemeSettings.clinicLocations.map(normalizeClinicLocationEntry);
    }

    const getClinicLocationLabel = (location, index) => {
        return String(location?.name || '').trim() || `Location ${index + 1}`;
    };

    const updatePreferredSlot = (index, field, value) => {
        if (appointmentFormError) {
            setAppointmentFormError("");
        }
        setPreferredSlots(prev => prev.map((slot, slotIndex) => (
            slotIndex === index ? { ...slot, [field]: value } : slot
        )));
    };

    const updateAppointmentField = (field, value) => {
        if (appointmentFormError) {
            setAppointmentFormError("");
        }
        if (consentFormError) {
            setConsentFormError("");
        }
        setUserData(prev => ({ ...prev, [field]: value }));
    };

    const updatePatientField = (field, value) => {
        if (appointmentFormError) {
            setAppointmentFormError("");
        }
        if (consentFormError) {
            setConsentFormError("");
        }
        setUserData(prev => ({ ...prev, [field]: value }));
    };

    const isValidAppointmentName = (value) => {
        const normalizedValue = value.trim();
        return normalizedValue.length >= 2 && /^[A-Za-z][A-Za-z\s'.-]*$/.test(normalizedValue);
    };

    const enforceWordLimit = (value, limit) => {
        const nextValue = String(value || '');
        const words = nextValue.match(/\S+/g) || [];
        if (words.length <= limit) return nextValue;
        return words.slice(0, limit).join(' ');
    };

    const isValidAppointmentEmail = (value) => {
        const normalizedValue = value.trim();
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedValue);
    };

    const isValidAppointmentPhone = (value) => {
        const normalizedValue = String(value || '').trim();
        return /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/.test(normalizedValue) && normalizedValue.replace(/\D/g, '').length >= 8;
    };

    const getDobFromAge = (ageValue) => {
        const normalizedAge = Number(ageValue);
        if (!Number.isFinite(normalizedAge)) return '';

        const birthYear = new Date().getFullYear() - normalizedAge;
        return `01-01-${birthYear}`;
    };

    const validateSmileTransformConsent = () => {
        const trimmedName = String(userData.name || '').trim();
        const trimmedContact = String(userData.contact || '').trim();
        const trimmedPhone = String(userData.phone || '').trim();
        const normalizedAge = Number(userData.age);

        if (!['male', 'female', 'non_binary', 'not_say'].includes(String(userData.gender || '').toLowerCase())) {
            return 'Please select the smile profile gender before continuing.';
        }

        if (!Number.isFinite(normalizedAge) || normalizedAge < 5 || normalizedAge > 110) {
            return 'Please choose an age between 5 and 110.';
        }

        if (!consentChecked) {
            return 'Please confirm the disclaimer and terms before continuing.';
        }

        if (!trimmedName) {
            return 'Please enter your full name for the transformation consent record.';
        }

        if (!isValidAppointmentName(trimmedName)) {
            return 'Please enter a proper name.';
        }

        if (!trimmedContact) {
            return 'Please enter your email address for the transformation consent record.';
        }

        if (!isValidAppointmentEmail(trimmedContact)) {
            return 'Please enter a proper email address.';
        }

        if (trimmedPhone && !isValidAppointmentPhone(trimmedPhone)) {
            return 'Please enter a valid phone number.';
        }

        return '';
    };

    const beginSmileTransformFromConsent = () => {
        const validationMessage = validateSmileTransformConsent();
        if (validationMessage) {
            setConsentFormError(validationMessage);
            return;
        }

        setConsentFormError('');
        const normalizedAge = Math.min(110, Math.max(5, Number(userData.age) || 25));
        const nextUserData = {
            ...userData,
            name: String(userData.name || '').trim(),
            contact: String(userData.contact || '').trim(),
            phone: String(userData.phone || '').trim(),
            age: normalizedAge,
            dob: getDobFromAge(normalizedAge),
            gender: String(userData.gender || '').toLowerCase()
        };
        userDataRef.current = nextUserData;
        setUserData(nextUserData);
        startImageTransform();
    };

    const confirmAppointment = () => {
        const selections = getPreferredSlotLabels();
        const trimmedName = userData.name.trim();
        const trimmedContact = userData.contact.trim();
        const trimmedPhone = userData.phone.trim();
        const configuredClinicLocations = getConfiguredClinicLocations();
        const selectedClinicLocation = configuredClinicLocations[selectedLocationIndex] || configuredClinicLocations[0] || null;
        const selectedClinicLocationName = selectedClinicLocation
            ? getClinicLocationLabel(selectedClinicLocation, Math.max(0, configuredClinicLocations.indexOf(selectedClinicLocation)))
            : '';
        const age = Number(userData.age) || null;
        setAppointmentFormError("");

        if (!trimmedName) {
            setAppointmentFormError("Please enter your full name before confirming the appointment.");
            return;
        }

        if (!isValidAppointmentName(trimmedName)) {
            setAppointmentFormError("Please enter a proper name.");
            return;
        }

        if (!trimmedContact) {
            setAppointmentFormError("Please enter your email address before confirming the appointment.");
            return;
        }

        if (!isValidAppointmentEmail(trimmedContact)) {
            setAppointmentFormError("Please enter a proper email address.");
            return;
        }

        if (trimmedPhone && !isValidAppointmentPhone(trimmedPhone)) {
            setAppointmentFormError("Please enter a valid phone number.");
            return;
        }

        if (preferredSlots.some(slot => !slot.day || !slot.time)) {
            setAppointmentFormError("Please select all three preferred appointment preferences.");
            return;
        }

        pendingAppointmentSlotsRef.current = selections;
        setIsConfirmingAppointment(true);
        setUserData(prev => ({ ...prev, name: trimmedName, contact: trimmedContact, phone: trimmedPhone, slots: selections, clinicLocation: selectedClinicLocationName }));
        sendWebSocketRequest('confirm_appointment', 'appointment_confirmed', {
            name: trimmedName,
            contact: trimmedContact,
            phone: trimmedPhone,
            age,
            dob: getDobFromAge(age),
            gender: String(userData.gender || '').trim(),
            slots: selections,
            clinicLocation: selectedClinicLocationName,
            clinicId: API_KEY,
            apiKey: API_KEY
        }, 30000);
    };

    const handleFinalBooking = () => {
        const selections = pendingAppointmentSlotsRef.current.length
            ? pendingAppointmentSlotsRef.current
            : userData.slots;
        setAppointmentFormError("");
        setBookingFlow('confirmed');
        setUserData(prev => ({ ...prev, slots: selections }));
    };

    const getEstimateCards = () => {
        if (!estimates) return [];
        const cards = [];

        // 1. Price Logic
        const { minPrice, maxPrice } = estimates;
        if (minPrice || maxPrice) {
            // If both exist and are different, show range. Otherwise show the one that exists.
            const value = (minPrice && maxPrice && minPrice !== maxPrice)
                ? `$${minPrice} — $${maxPrice}`
                : `$${minPrice || maxPrice}`;
            cards.push({ label: 'Estimated Treatment Cost', value, type: 'price' });
        }

        // 2. Timeline Logic
        const { minWeeks, maxWeeks } = estimates;
        if (minWeeks || maxWeeks) {
            const value = (minWeeks && maxWeeks && minWeeks !== maxWeeks)
                ? `${minWeeks} — ${maxWeeks} Weeks`
                : `${minWeeks || maxWeeks} Weeks`;
            cards.push({ label: 'Expected Timeline', value, type: 'time' });
        }
        return cards;
    };

    const createAssessmentImageBlob = async () => {
        if (!assessmentRef.current) {
            throw new Error("Assessment section is not available for capture.");
        }

        const blob = await toBlob(assessmentRef.current, {
            cacheBust: true,
            pixelRatio: 2,
            backgroundColor: themeSettings.primaryBg || '#ffffff',
            style: {
                ...themeStyle,
                transform: 'none',
                transformOrigin: 'top left',
                width: `${assessmentRef.current.scrollWidth}px`,
                height: `${assessmentRef.current.scrollHeight}px`
            },
            filter: (node) => {
                return !(
                    node instanceof HTMLElement &&
                    node.dataset &&
                    node.dataset.excludeFromDownload === 'true'
                );
            }
        });

        if (!blob) throw new Error("Unable to create assessment image.");
        return blob;
    };

    const blobFromDataUrl = async (dataUrl) => {
        // const response = await fetch(dataUrl);
        const response = await fetch(dataUrl, {
            method: 'GET',
            cache: 'reload' // Forces the browser to grab a fresh copy from the network
        });
        return response.blob();
    };

    const addCacheBuster = (url) => {
        const rawUrl = String(url || '').trim();
        if (!rawUrl) return rawUrl;

        try {
            const parsedUrl = new URL(rawUrl, window.location.href);
            parsedUrl.searchParams.set('_cb', String(Date.now()));
            return parsedUrl.href;
        } catch {
            const separator = rawUrl.includes('?') ? '&' : '?';
            return `${rawUrl}${separator}_cb=${Date.now()}`;
        }
    };

    const getImageBlob = async (source) => {
        if (!source) return null;
        if (source.startsWith('data:')) return blobFromDataUrl(source);

        try {
            const cacheBustedSource = addCacheBuster(source);
            const response = await fetch(cacheBustedSource, { mode: 'cors', cache: 'no-store' });
            if (!response.ok) throw new Error(`Unable to download image: ${response.status}`);
            return response.blob();
        } catch (err) {
            const message = String(err?.message || err || '');
            if (/failed to fetch|networkerror|cors/i.test(message)) {
                throw new Error('Unable to load the selected stock image from S3. Please enable CORS on the bucket for your app origin.');
            }
            throw err;
        }
    };

    const getImageExtension = (blob, fallback = 'jpg') => {
        if (!blob?.type) return fallback;
        if (blob.type.includes('png')) return 'png';
        if (blob.type.includes('webp')) return 'webp';
        if (blob.type.includes('jpeg') || blob.type.includes('jpg')) return 'jpg';
        return fallback;
    };

    const isLikelyImageKey = (key) => {
        return /\.(avif|gif|jpe?g|png|webp|bmp|tiff?)$/i.test(String(key || ''));
    };

    const loadStockImages = async () => {
        if (stockImagesLoading) return;

        setStockImagesError('');
        setStockImagesLoading(true);

        try {
            const response = await fetch(STOCK_IMAGES_LIST_URL, { mode: 'cors' });
            if (!response.ok) {
                throw new Error(`Unable to list stock images: ${response.status}`);
            }

            const xmlText = await response.text();
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
            const parseError = xmlDoc.querySelector('parsererror');
            if (parseError) {
                throw new Error('Unable to parse stock image listing.');
            }

            const images = Array.from(xmlDoc.querySelectorAll('Contents'))
                .map((entry) => {
                    const key = entry.querySelector('Key')?.textContent?.trim() || '';
                    if (!key || !isLikelyImageKey(key)) return null;

                    const encodedKey = key
                        .split('/')
                        .map((segment) => encodeURIComponent(segment))
                        .join('/');

                    return {
                        key,
                        url: new URL(encodedKey, STOCK_IMAGES_BUCKET_URL).href,
                        name: key.split('/').pop() || key
                    };
                })
                .filter(Boolean);

            setStockImages(images);
            if (!images.length) {
                setStockImagesError('No stock images were found in the bucket.');
            }
        } catch (err) {
            console.error(err);
            const message = String(err?.message || err || '');
            if (/failed to fetch|networkerror|cors/i.test(message)) {
                setStockImagesError('Unable to load stock images from S3. Please enable CORS on the bucket for your app origin.');
            } else {
                setStockImagesError(err?.message || 'Unable to load stock images.');
            }
        } finally {
            setStockImagesLoading(false);
        }
    };

    const openStockImagePicker = async () => {
        setShowStockImagePicker(true);
        if (!stockImages.length) {
            await loadStockImages();
        }
    };

    const closeStockImagePicker = () => {
        setShowStockImagePicker(false);
        setStockImagesError('');
    };

    const useSelectedStockImage = async () => {
        if (!selectedStockImageUrl) return;

        try {
            setStockImagesError('');
            const blob = await getImageBlob(selectedStockImageUrl);
            if (!blob) {
                throw new Error('Unable to load the selected stock image.');
            }

            const extension = getImageExtension(blob);
            const fileName = `stock-image-${Date.now()}.${extension}`;
            const file = new File([blob], fileName, { type: blob.type || `image/${extension}` });

            closeStockImagePicker();
            processImage(file);
        } catch (err) {
            console.error(err);
            setStockImagesError(err?.message || 'Unable to use the selected stock image.');
        }
    };

    const downloadSmilePackage = async () => {
        if (!image || !results.length || isPreparingDownload) return;

        setIsPreparingDownload(true);
        setError(null);

        try {
            const zip = new JSZip();

            const originalBlob = await getImageBlob(image);
            if (originalBlob) {
                zip.file(`original-smile.${getImageExtension(originalBlob)}`, originalBlob);
            }

            for (const [index, result] of results.slice(0, 2).entries()) {
                const imageBlob = await getImageBlob(result);
                const filename = index === 0 ? 'natural-aesthetic' : 'Professional-enhancement';
                zip.file(`${filename}.${getImageExtension(imageBlob)}`, imageBlob);
            }

            const assessmentBlob = await createAssessmentImageBlob();
            zip.file('personalized-assessment.png', assessmentBlob);

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            saveAs(zipBlob, `smile-assessment-package-${Date.now()}.zip`);
        } catch (err) {
            console.error(err);
            setError("We couldn't prepare the download package. Please try again.");
        } finally {
            setIsPreparingDownload(false);
        }
    };

    const estimateCards = getEstimateCards();
    const visibleEstimateCards = estimateCards.filter((card) => {
        if (card.type === 'price') return activeThemeSettings.showAssessmentPrice;
        if (card.type === 'time') return activeThemeSettings.showAssessmentTimeline;
        return true;
    });
    const displayedSpecialists = triageData?.specialists || userData.triage?.specialists || [];
    const normalizeClinicOption = (value) => {
        const unwrappedValue = unwrapClinicAttributeValue(value);
        if (unwrappedValue === null || unwrappedValue === undefined || unwrappedValue === '') return null;
        if (typeof unwrappedValue === 'string') return unwrappedValue.trim();
        if (typeof unwrappedValue === 'number' || typeof unwrappedValue === 'boolean') return String(unwrappedValue);
        if (typeof unwrappedValue === 'object') {
            return unwrappedValue.label || unwrappedValue.value || unwrappedValue.name || null;
        }
        return String(unwrappedValue);
    };

    const getClinicOptions = (value) => {
        const unwrappedValue = unwrapClinicAttributeValue(value);
        if (!unwrappedValue) return [];

        if (typeof unwrappedValue === 'string') {
            try {
                const parsedValue = JSON.parse(unwrappedValue);
                return getClinicOptions(parsedValue);
            } catch {
                return unwrappedValue.split(',')
                    .map(item => normalizeClinicOption(item))
                    .filter(Boolean);
            }
        }

        if (Array.isArray(unwrappedValue)) {
            return unwrappedValue
                .map(item => normalizeClinicOption(item))
                .filter(Boolean);
        }

        if (typeof unwrappedValue === 'object') {
            return Object.values(unwrappedValue)
                .map(item => normalizeClinicOption(item))
                .filter(Boolean);
        }

        return [normalizeClinicOption(unwrappedValue)].filter(Boolean);
    };

    const getClinicLocations = () => {
        if (Array.isArray(currentThemeSettings.clinicLocations) && currentThemeSettings.clinicLocations.length) {
            return currentThemeSettings.clinicLocations.map(normalizeClinicLocationEntry);
        }

        return [normalizeClinicLocationEntry({})];
    };

    const getClinicPhoneNumber = (location) => {
        if (!location) return '';

        const configuredPhoneParts = [
            location.phoneCountryCode,
            location.phoneNumber
        ]
            .map(part => normalizeLocationField(part))
            .filter(Boolean);

        return configuredPhoneParts.length ? configuredPhoneParts.join(' ') : '';
    };

    const getClinicWhatsappNumber = (location) => {
        if (!location) return '';
        if (location.whatsappSameAsPhone) return getClinicPhoneNumber(location);

        const configuredWhatsappParts = [
            location.whatsappCountryCode,
            location.whatsappNumber
        ]
            .map(part => normalizeLocationField(part))
            .filter(Boolean);

        return configuredWhatsappParts.length ? configuredWhatsappParts.join(' ') : '';
    };

    const getTelHref = (phoneNumber) => {
        const normalizedPhoneNumber = String(phoneNumber || '').replace(/[^\d+]/g, '');
        return normalizedPhoneNumber ? `tel:${normalizedPhoneNumber}` : '';
    };

    const getWhatsappHref = (phoneNumber) => {
        const normalizedPhoneNumber = String(phoneNumber || '').replace(/\D/g, '');
        if (!normalizedPhoneNumber) return '';

        const message = 'Hi, I would like to book a smile consultation.';
        return `https://wa.me/${normalizedPhoneNumber}?text=${encodeURIComponent(message)}`;
    };

    const getMapDataForLocation = (location, zoomScale = 1) => {
        if (!location) return null;
        const lat = Number(location.latitude);
        const lng = Number(location.longitude);
        const hasValidCoordinates = Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
        if (!hasValidCoordinates) return null;

        const safeZoomScale = Math.min(4, Math.max(0.25, Number(zoomScale) || 1));
        const latDelta = 0.004 * safeZoomScale;
        const lngDelta = 0.006 * safeZoomScale;
        const bbox = [
            lng - lngDelta,
            lat - latDelta,
            lng + lngDelta,
            lat + latDelta
        ].join(',');

        return {
            ...location,
            lat,
            lng,
            embedUrl: `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`,
            openUrl: `https://www.google.com/maps/search/?api=1&query=${lat},${lng}&zoom=17`
        };
    };

    const handleMapWheel = (locationIndex, event) => {
        if (!event.ctrlKey) return;

        event.preventDefault();
        event.stopPropagation();

        const zoomStep = 1.15;
        setMapZoomLevels(prev => {
            const currentZoom = prev[locationIndex] || 1;
            const nextZoom = event.deltaY < 0 ? currentZoom / zoomStep : currentZoom * zoomStep;
            return {
                ...prev,
                [locationIndex]: Math.min(4, Math.max(0.25, Number(nextZoom.toFixed(3))))
            };
        });
    };

    // Prevent browser-level zoom when ctrl+scrolling over mapped iframes.
    // We attach a capture-phase listener so we can call preventDefault() before the browser handles zoom.
    useEffect(() => {
        const handler = (e) => {
            try {
                if (!e.ctrlKey) return;
                const mapElem = e.target && e.target.closest && e.target.closest('[data-falcon-map]');
                if (!mapElem) return;
                // Prevent the browser from zooming the whole page; allow React bubble handlers to run.
                e.preventDefault();
            } catch (err) {
                // ignore
            }
        };

        document.addEventListener('wheel', handler, { passive: false, capture: true });
        return () => document.removeEventListener('wheel', handler, { capture: true });
    }, []);

    // Slot options always come from the clinic data loaded at startup, not from chat replies.
    const clinicLocations = showLocations ? getClinicLocations() : [];
    const appointmentClinicLocations = showLocations ? getConfiguredClinicLocations() : [];
    useEffect(() => {
        if (selectedLocationIndex >= clinicLocations.length) {
            setSelectedLocationIndex(0);
        }
    }, [clinicLocations.length, selectedLocationIndex]);

    const availableDays = getClinicOptions(clinicData?.days);
    const availableHours = APPOINTMENT_TIME_INTERVALS;

    useEffect(() => {
        if (!availableDays.length) return;

        setPreferredSlots(prev => prev.map((slot, index) => {
            const defaultSlot = getDefaultPreferredSlots(availableDays, availableHours)[index] || { day: '', time: '' };

            return {
                day: slot.day || defaultSlot.day,
                time: slot.time || defaultSlot.time
            };
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicData?.days]);

    const generationSteps = [
        { key: 'prepare', label: 'Preparing image', description: 'Optimizing the photo before upload.' },
        { key: 'url', label: 'Generating secure URL', description: 'Requesting a private S3 upload link.' },
        { key: 'upload', label: 'Uploading image', description: 'Sending the optimized photo securely.' },
        { key: 'transform', label: 'Transforming smile', description: 'Creating your AI smile concepts.' },
        { key: 'finalize', label: 'Preparing results', description: 'Organizing the images and assessment.' }
    ];

    const getGenerationStepIndex = () => {
        return Math.max(0, generationSteps.findIndex(step => step.key === generationProgress.step));
    };

    const getGenerationOverallProgress = () => {
        const step = generationProgress.step;
        if (step === 'prepare') return 10;
        if (step === 'url') return 25;
        if (step === 'upload') return 30 + Math.round(generationProgress.uploadProgress * 0.35);
        if (step === 'transform') return 82;
        if (step === 'finalize') return 96;
        return 5;
    };

    const getFormattedTransformTimeLeft = () => {
        const minutes = Math.floor(transformTimeLeft / 60);
        const seconds = transformTimeLeft % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const getTransformAnalysisStage = () => {
        const totalDuration = TRANSFORM_DURATION_SECONDS;
        const elapsedSeconds = Math.max(0, totalDuration - transformTimeLeft);
        const segmentDuration = Math.max(1, Math.floor(totalDuration / TRANSFORM_ANALYSIS_STAGES.length));
        const stageIndex = Math.min(
            TRANSFORM_ANALYSIS_STAGES.length - 1,
            Math.floor(elapsedSeconds / segmentDuration)
        );

        return TRANSFORM_ANALYSIS_STAGES[stageIndex];
    };

    const renderGenerationProgress = ({ title, subtitle, icon = 'sparkles' }) => {
        const activeStepIndex = getGenerationStepIndex();
        const activeStep = generationSteps[activeStepIndex] || generationSteps[0];
        const progressValue = getGenerationOverallProgress();
        const activeAnalysisStage = generationProgress.step === 'transform'
            ? getTransformAnalysisStage()
            : generationProgress.step === 'finalize'
                ? TRANSFORM_ANALYSIS_STAGES[TRANSFORM_ANALYSIS_STAGES.length - 1]
                : null;

        // Compact view shown when details are closed
        if (!generationDetailsOpen) {
            return (
                <div className="w-full max-w-xl mx-auto rounded-[2.5rem] border border-border bg-screen text-screen shadow-2xl p-4 md:p-6">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h3 className="text-lg font-black">{title}</h3>
                            <p className="text-xs text-screen mt-1">{subtitle}</p>
                        </div>
                        <div className="text-sm font-black text-highlight">{progressValue}%</div>
                    </div>

                    <div className="mb-3">
                        <div className="h-3 rounded-full bg-border-20 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-highlight transition-all duration-500"
                                style={{ width: `${progressValue}%` }}
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-sm font-black">{activeStep.label}</p>
                            {generationProgress.step === 'upload' && (
                                <p className="text-xs text-primary">Upload: {generationProgress.uploadProgress}%</p>
                            )}
                        </div>
                        <div className="text-right">
                            {generationProgress.step === 'transform' && (
                                <div className="inline-flex items-center gap-2 rounded-full border border-border bg-highlight px-3 py-1 text-sm font-black text-highlight">
                                    <RefreshCw size={12} className="animate-spin" />
                                    <span>{getFormattedTransformTimeLeft()}</span>
                                </div>
                            )}
                            <button type="button" onClick={() => setGenerationDetailsOpen(true)} className="text-[12px] font-bold text-screen underline ml-3">Show more details</button>
                        </div>
                    </div>
                    {activeAnalysisStage && (
                        <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-border bg-highlight px-3 py-1 text-sm font-black text-highlight">
                            <span className="font-black">{activeAnalysisStage.label}</span>
                        </div>
                    )}
                </div>
            );
        }

        // Expanded view (original detailed content)
        return (
            <div className="w-full max-w-xl mx-auto rounded-[2.5rem] border border-border bg-screen text-primary shadow-2xl p-6 md:p-8 space-y-7">
                <div className="flex flex-col items-center text-center gap-5">
                    <div>
                        <h2 className="text-2xl font-black text-screen">{title}</h2>
                        <p className="text-screen mt-2">{subtitle}</p>
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="flex items-center justify-between text-[12px] font-black uppercase tracking-widest text-highlight">
                        <span>{activeStep.label}</span>
                        <span>{progressValue}%</span>
                    </div>
                    <div className="h-3 rounded-full bg-border-20 overflow-hidden">
                        <div
                            className="h-full rounded-full bg-highlight transition-all duration-500"
                            style={{ width: `${progressValue}%` }}
                        />
                    </div>
                    <p className="text-sm text-highlight">{activeStep.description}</p>
                    {generationProgress.step === 'upload' && (
                        <p className="text-sm font-black text-highlight">Upload progress: {generationProgress.uploadProgress}%</p>
                    )}
                    {generationProgress.step === 'transform' && (
                        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-highlight px-4 py-2 text-sm font-black text-highlight">
                            <RefreshCw size={14} className="animate-spin" />
                            <span>Time left: {getFormattedTransformTimeLeft()}</span>
                        </div>
                    )}
                </div>

                {activeAnalysisStage && (
                    <div className="rounded-[2rem] border border-border bg-highlight px-5 py-4 transition-all duration-700 ease-in-out">
                        <p className="text-[11px] font-black uppercase tracking-[0.2em] text-highlight mb-2">Live Clinical Analysis</p>
                        <p className="text-base font-black text-primary animate-pulse">{activeAnalysisStage.label}</p>
                        <p className="text-sm text-primary mt-2 leading-relaxed">{activeAnalysisStage.detail}</p>
                    </div>
                )}

                <div className="grid gap-3">
                    {generationSteps.map((step, index) => {
                        const isComplete = index < activeStepIndex;
                        const isActive = index === activeStepIndex;

                        return (
                            <div
                                key={step.key}
                                className={`flex items-center gap-3 rounded-2xl border border-border p-3 transition-all ${isActive ? 'bg-highlight' : 'bg-primary'}`}
                            >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-black border border-border ${isComplete || isActive ? 'bg-highlight text-highlight' : 'bg-primary text-primary'}`}>
                                    {isComplete ? 'OK' : index + 1}
                                </div>
                                <div className="min-w-0">
                                    <p className={`text-sm font-black ${isActive ? 'text-highlight' : 'text-primary'}`}>{step.label}</p>
                                    <p className="text-[12px] text-primary">{step.description}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="text-right">
                    <button type="button" onClick={() => setGenerationDetailsOpen(false)} className="text-[12px] font-bold text-primary underline">Hide details</button>
                </div>
            </div>
        );
    };

    return (
        <div ref={iframeContentRef} className="falcon-smile-theme bg-screen text-screen font-sans p-3 sm:p-4 md:p-8 relative" style={themeStyle}>
            {!reviewMode && <button
                type="button"
                onClick={openSettingsPanel}
                className="absolute top-4 right-4 z-[180] bg-primary text-primary border border-border shadow-xl rounded-2xl p-3 hover:bg-highlight hover:text-highlight transition-all"
                aria-label="Open settings"
            >
                <Settings size={22} />
            </button>}

            {showSettingsPanel && (
                <SettingsPanel
                    currentDomainName={CURRENT_DOMAIN_NAME}
                    isSavingTheme={isSavingTheme}
                    setIsSavingTheme={setIsSavingTheme}
                    onClose={() => setShowSettingsPanel(false)}
                    draftThemeSettings={draftThemeSettings}
                    setDraftThemeSettings={setDraftThemeSettings}
                    setThemeSettings={setThemeSettings}
                    fontOptions={fontOptions}
                    defaultThemeSettings={defaultThemeSettings}
                    enforceWordLimit={enforceWordLimit}
                    clinicData={clinicData}
                    setClinicData={setClinicData}
                    clinicKey={API_KEY}
                    sendHttpRequest={sendHttpRequest}
                    unwrapClinicAttributeValue={unwrapClinicAttributeValue}
                    setError={setError}
                />
            )}
            {/* Initial Loading Overlay - Plugin Loading */}
            {isInitialLoading && (
                <div className="fixed inset-0 z-[200] bg-screen backdrop-blur-sm flex flex-col items-center justify-center">
                    <div className="relative">
                        <div className="w-32 h-32 border-[8px] border-border-20 border-t-highlight rounded-full animate-spin"></div>
                        <Sparkles className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-highlight animate-pulse" size={48} />
                    </div>
                    <div className="text-center mt-12 px-4">
                        <h2 className="text-3xl font-black text-primary">Please wait</h2>
                        <p className="text-screen mt-2 text-lg">plug-in is loading</p>
                    </div>
                </div>
            )}

            {connectionRetryError && (
                <div className="fixed inset-0 z-[210] bg-screen text-primary flex items-start sm:items-center justify-center p-4 sm:p-6 md:p-10 overflow-y-auto">
                    <div className="w-full max-w-2xl rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[3rem] border border-border bg-primary shadow-2xl p-5 sm:p-6 md:p-12 text-center space-y-6 sm:space-y-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
                        <div className="mx-auto w-24 h-24 rounded-full bg-warning border border-warning-30 flex items-center justify-center">
                            <AlertCircle size={40} className="text-warning" />
                        </div>

                        <div className="space-y-3">
                            <p className="text-3xl font-black text-primary">Connection Issue</p>
                            <p className="text-base text-primary leading-relaxed">{connectionRetryError.message}</p>
                            <p className="text-sm text-primary">Please check your internet connection and try the action again.</p>
                        </div>

                        <div className="rounded-[2rem] border border-border p-5 text-left">
                            <p className="text-xs font-black uppercase tracking-widest text-highlight mb-2">What to check</p>
                            <p className="text-sm text-primary leading-relaxed">
                                Confirm the clinical endpoint is reachable and that this website is allowed to connect.
                            </p>
                        </div>

                        <div className="flex justify-center">
                            <button
                                onClick={() => setConnectionRetryError(null)}
                                className="min-w-[220px] py-4 px-8 rounded-2xl bg-warning text-warning font-black hover:bg-warning transition-all"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Retry Error Modal - Timeout/Failed Requests */}
            {retryableError && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-screen text-info backdrop-blur-sm">
                    <div className="bg-primary text-primary rounded-[1.5rem] sm:rounded-[2.5rem] shadow-2xl max-w-lg w-full overflow-hidden max-h-[calc(100vh-2rem)] overflow-y-auto">
                        <div className="bg-warning p-6 text-warning flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <AlertCircle size={28} />
                                <div>
                                    <p className="text-lg font-black">Connection Issue</p>
                                    <p className="text-sm text-warning">Request could not be completed</p>
                                </div>
                            </div>
                            <button onClick={() => setRetryableError(null)} className="p-2 rounded-lg bg-primary text-primary hover:bg-primary">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-5 sm:p-8 space-y-5 sm:space-y-6">
                            <p className="text-primary leading-relaxed">{retryableError.message}</p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <button
                                    onClick={() => {
                                        setRetryableError(null);
                                        // Retry the failed request
                                        sendWebSocketRequest(retryableError.action, retryableError.response, retryableError.data, retryableError.timeoutMs);
                                    }}
                                    className="flex-1 py-4 rounded-2xl bg-warning text-warning font-black hover:bg-warning transition-all"
                                >
                                    Retry
                                </button>
                                <button
                                    onClick={() => setRetryableError(null)}
                                    className="flex-1 py-4 rounded-2xl border border-border text-primary font-black hover:bg-info hover:text-info transition-all"
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto">
                {isRegenerating && (
                    <div className="fixed inset-0 z-[100] bg-primary backdrop-blur-md flex flex-col items-center justify-center animate-in fade-in duration-300">
                        {renderGenerationProgress({
                            title: 'Refreshing Your Smile Concepts',
                            subtitle: 'We are updating each stage as your new concepts are regenerated.',
                            icon: 'refresh'
                        })}
                    </div>
                )}

                <div className={`${viewerImage ? 'hidden' : 'flex'} w-full max-w-7xl mx-auto flex-col gap-6 sm:gap-8 lg:gap-12`}>

                    <header className="text-center">
                        {/* <div className="inline-flex items-center gap-2 bg-highlight text-highlight px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-4">
                            <Sparkles size={12} /> {activeThemeSettings.heroBadgeText}
                        </div> */}
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-screen mb-2 sm:mb-3 tracking-tight">
                            {activeThemeSettings.heroTitle}
                        </h1>
                        <p className="text-screen max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
                            {activeThemeSettings.heroSubtitle}
                        </p>
                    </header>

                    {!image && !isCapturing && !pendingImageAction && (
                        <div style={{ minWidth: '250px' }} className="w-[90%] bg-primary text-primary rounded-[1.5rem] sm:rounded-[1.75rem] md:rounded-[2.5rem] shadow-2xl p-6 sm:p-6 md:p-20 text-center border-2 border-border transition-all max-w-4xl mx-auto">
                            <div className="flex flex-col md:flex-row gap-5 sm:gap-6 md:gap-8 justify-center items-center">
                                <button onClick={() => openGuideForAction('camera')} className="flex flex-col items-center gap-3 sm:gap-4 p-6 sm:p-6 md:p-8 w-full md:w-72 rounded-3xl bg-highlight hover:bg-highlight text-highlight font-bold transition-all shadow-xl shadow-highlight transform hover:-translate-y-1 text-base sm:text-base"><Camera size={36} className="sm:w-10 sm:h-10" /> Take photo</button>
                                <div className="text-primary font-black hidden md:block text-2xl"> or </div>
                                <button onClick={() => openGuideForAction('upload')} className="flex flex-col items-center gap-3 sm:gap-4 p-6 sm:p-6 md:p-8 w-full md:w-72 rounded-3xl bg-primary border-2 border-border text-primary font-bold transition-all shadow-xl shadow-info transform hover:-translate-y-1 text-base sm:text-base"><Upload size={36} className="sm:w-10 sm:h-10" /> Upload Photo</button>
                            </div>
                            {reviewMode && (
                                <div className="mt-5 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => openGuideForAction('stock')}
                                        className="inline-flex items-center justify-center rounded-full border border-border bg-info px-4 py-2 text-[11px] sm:text-xs font-black uppercase tracking-widest text-primary shadow-lg transition-all hover:bg-highlight hover:text-highlight"
                                    >
                                        Select from stock images
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileUpload}
                        onCancel={clearSmileSessionTimer}
                    />

                    {showStockImagePicker && (
                        <div className="fixed inset-0 z-[140] flex items-start sm:items-center justify-center bg-screen/75 backdrop-blur-md p-3 sm:p-4 overflow-y-auto">
                            <div className="w-full max-w-5xl rounded-[1.75rem] border border-border bg-primary text-primary shadow-2xl overflow-hidden max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] flex flex-col">
                                <div className="flex items-center justify-between gap-4 border-b border-border px-5 sm:px-6 py-5">
                                    <div className="min-w-0">
                                        <p className="text-[11px] font-black uppercase tracking-[0.2em] text-highlight">Stock Images</p>
                                        <h3 className="mt-1 text-xl sm:text-2xl font-black text-primary">Choose one image from the public bucket</h3>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={closeStockImagePicker}
                                        className="shrink-0 p-3 rounded-2xl border border-border bg-primary text-primary hover:bg-highlight hover:text-highlight transition-all"
                                        aria-label="Close stock image picker"
                                    >
                                        <X size={22} />
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto p-5 sm:p-6">
                                    {stockImagesLoading && (
                                        <div className="py-14 text-center text-sm font-bold text-primary">
                                            Loading stock images...
                                        </div>
                                    )}

                                    {!stockImagesLoading && stockImagesError && (
                                        <div className="rounded-2xl border border-warning bg-warning px-4 py-3 text-sm font-bold text-warning">
                                            {stockImagesError}
                                        </div>
                                    )}

                                    {!stockImagesLoading && !stockImagesError && stockImages.length > 0 && (
                                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                            {stockImages.map((item) => {
                                                const isSelected = selectedStockImageUrl === item.url;
                                                return (
                                                    <button
                                                        key={item.key}
                                                        type="button"
                                                        onClick={() => setSelectedStockImageUrl(item.url)}
                                                        className={`group overflow-hidden rounded-[1.5rem] border-2 bg-screen text-left shadow-lg transition-all ${isSelected ? 'border-highlight ring-4 ring-highlight/20' : 'border-border hover:border-highlight'}`}
                                                    >
                                                        <div className="aspect-[4/5] w-full overflow-hidden bg-primary">
                                                            <img
                                                                src={item.url}
                                                                alt={item.name}
                                                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                                                loading="lazy"
                                                            />
                                                        </div>
                                                        <div className="flex items-center justify-between gap-3 px-4 py-3">
                                                            {/* <span className="min-w-0 truncate text-[12px] font-black text-primary">{item.name}</span> */}
                                                            <span className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest ${isSelected ? 'bg-highlight text-highlight' : 'bg-info text-primary'}`}>
                                                                {isSelected ? 'Selected' : 'Select'}
                                                            </span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {!stockImagesLoading && !stockImagesError && stockImages.length === 0 && (
                                        <div className="py-14 text-center text-sm font-bold text-primary/80">
                                            No stock images available.
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 border-t border-border px-5 sm:px-6 py-4">
                                    <button
                                        type="button"
                                        onClick={closeStockImagePicker}
                                        className="w-full sm:w-auto rounded-2xl border border-border bg-primary px-5 py-3 text-sm font-black text-primary transition-all hover:bg-info"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={useSelectedStockImage}
                                        disabled={!selectedStockImageUrl}
                                        className="w-full sm:w-auto rounded-2xl bg-highlight px-5 py-3 text-sm font-black text-highlight transition-all hover:bg-highlight disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Use Selected Image
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {pendingImageAction && (
                        <div ref={guideModalRef} className="w-full flex justify-center scroll-mt-4">
                            <div className="w-full max-w-[720px] bg-primary text-primary rounded-[1.75rem] md:rounded-[2.5rem] border border-border shadow-2xl overflow-hidden flex flex-col">
                                <div className="flex items-center justify-between gap-4 border-b border-border px-6 py-5">
                                    <div>
                                        <p className="text-[11px] font-black uppercase tracking-[0.2em] text-highlight">Photo Guide & Disclaimer</p>
                                        <h3 className="mt-1 text-xl md:text-2xl font-black text-primary">Follow this guide before continuing</h3>
                                    </div>
                                    <button
                                        onClick={closeGuide}
                                        className="shrink-0 p-3 rounded-2xl border border-border bg-primary text-primary hover:bg-highlight hover:text-highlight transition-all"
                                        aria-label="Close guide"
                                    >
                                        <X size={22} />
                                    </button>
                                </div>

                                <div className="p-5 md:p-8 space-y-6">
                                    <img
                                        src={guideImage}
                                        alt='Guide'
                                        className="w-full max-h-[58vh] object-contain"
                                    />

                                    <div className="rounded-[1.5rem] border border-border bg-primary-40 p-4 space-y-4">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-screen">Consent Record</p>
                                        <div className="grid gap-3 lg:grid-cols-3">
                                            <input
                                                type="text"
                                                value={userData.name}
                                                onChange={(event) => updatePatientField('name', event.target.value)}
                                                placeholder="Full name"
                                                className="w-full rounded-2xl border border-border bg-primary px-4 py-3 text-sm font-bold text-primary outline-none focus:ring-2 focus:ring-border-100"
                                            />
                                            <input
                                                type="email"
                                                value={userData.contact}
                                                onChange={(event) => updatePatientField('contact', event.target.value)}
                                                placeholder="Email address"
                                                className="w-full rounded-2xl border border-border bg-primary px-4 py-3 text-sm font-bold text-primary outline-none focus:ring-2 focus:ring-border-100"
                                            />
                                            <input
                                                type="tel"
                                                value={userData.phone}
                                                onChange={(event) => updatePatientField('phone', event.target.value)}
                                                placeholder="Phone number (optional)"
                                                className="w-full rounded-2xl border border-border bg-primary px-4 py-3 text-sm font-bold text-primary outline-none focus:ring-2 focus:ring-border-100"
                                            />
                                        </div>
                                        <p className="rounded-xl bg-info-10 px-3 py-2 text-[11px] font-black leading-snug text-screen">
                                            We&apos;ll email your AI smile images here.
                                        </p>
                                        <label className="block rounded-2xl border border-border bg-primary p-4 cursor-pointer transition-colors text-sm text-primary leading-relaxed">
                                            <input type="checkbox" className="float-left mr-3 mt-1 w-5 h-5 rounded border-border text-highlight" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />
                                            <span>
                                                I understand this AI smile preview is informational only, not a diagnosis, treatment plan, or guarantee. I consent to HIPAA & PHIPAA data handling and agree to the <a href="https://ai.dentallive.com/terms.html" target="_blank" rel="noreferrer" className="text-blue-500 underline hover:no-underline">Terms and Conditions</a>.
                                            </span>
                                        </label>
                                    </div>

                                    {consentFormError && (
                                        <p className="rounded-2xl border border-warning bg-warning px-4 py-3 text-sm font-bold text-warning">
                                            {consentFormError}
                                        </p>
                                    )}

                                    <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                                        <button
                                            onClick={closeGuide}
                                            className="px-6 py-3 rounded-2xl border border-border text-primary font-black transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={proceedWithGuidedAction}
                                            disabled={!consentChecked}
                                            className="px-6 py-3 rounded-2xl bg-highlight text-highlight font-black shadow-lg transition-all disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            Proceed
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {isCapturing && (
                        <div className="relative rounded-[1.75rem] md:rounded-[2.5rem] overflow-hidden shadow-2xl bg-primary text-primary aspect-[3/4] sm:aspect-video min-h-[50vh] max-h-[78vh] max-w-md sm:max-w-3xl mx-auto ring-8 ring-border-100">
                            <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <div
                                    className="h-[88%] w-auto aspect-[3/4] max-w-[72%] sm:max-w-[42%] min-w-[170px] rounded-[50%] border-4 border-highlight shadow-[0_0_0_9999px_rgba(15,23,42,0.48)]"
                                    aria-hidden="true"
                                />
                            </div>
                            <div className="pointer-events-none absolute inset-x-4 top-2 m-4 flex justify-center">
                                <div className="w-full rounded-2xl p-2 bg-primary-90  text-center text-primary shadow-xl border border-border">
                                    <p className="text-sm font-bold">Move closer until your face fills most of the oval. Smile, then capture.</p>
                                </div>
                            </div>
                            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4 sm:gap-6">
                                {cameraDevices.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={switchCamera}
                                        disabled={isSwitchingCamera}
                                        className="bg-primary text-primary p-5 sm:p-6 rounded-full shadow-2xl hover:scale-110 transition-all disabled:cursor-not-allowed disabled:opacity-60"
                                        aria-label="Flip camera"
                                        title="Flip camera"
                                    >
                                        <SwitchCamera size={32} className={isSwitchingCamera ? 'animate-pulse' : ''} />
                                    </button>
                                )}
                                <button onClick={capturePhoto} className="bg-primary text-primary p-6 rounded-full shadow-2xl hover:scale-110 transition-all" aria-label="Capture aligned smile photo"><Camera size={36} /></button>
                                <button onClick={cancelCameraCapture} className="bg-warning text-warning p-6 rounded-full shadow-2xl hover:scale-110 transition-all" aria-label='Cancel'><X size={36} /></button>
                            </div>
                        </div>
                    )}

                    {image && results.length === 0 && !loading && (
                        <div className="bg-primary text-primary rounded-[1.75rem] md:rounded-[2.5rem] shadow-2xl p-8 w-full max-w-xl mx-auto text-center border border-border">
                            <div className="w-full h-50 relative rounded-3xl overflow-hidden mb-8 shadow-inner ring-4 ring-border-14">
                                <img src={visualOverlayBase64Image || image} alt="Original" className="w-full max-h-[500px] object-cover" />
                            </div>
                            <div className="flex flex-col gap-5">
                                <div className="rounded-[1.5rem] border border-border bg-primary-40 p-4 text-left space-y-4">
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-info">AI Smile Profile</p>
                                        <p className="mt-1 text-[12px] font-semibold text-primary">Age and gender help tune the preview to natural facial and dental proportions.</p>
                                    </div>
                                    <div className="grid gap-4">
                                        <div className="block space-y-2">
                                            <span className="text-[10px] font-black uppercase tracking-widest text-primary">Gender</span>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                {[
                                                    { label: 'Male', value: 'male' },
                                                    { label: 'Female', value: 'female' },
                                                    { label: 'Non-binary', value: 'non_binary' },
                                                    { label: 'Rather not say', value: 'not_say' }
                                                ].map((option) => {
                                                    const selectedGender = String(userData.gender || '');
                                                    const isSelectedGender = selectedGender === option.value;

                                                    return (
                                                        <label
                                                            key={option.value}
                                                            className={`flex cursor-pointer items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold transition-all ${isSelectedGender
                                                                ? 'border-highlight bg-info text-primary'
                                                                : 'border-border bg-primary text-primary'
                                                                }`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="smile-profile-gender"
                                                                value={option.value}
                                                                checked={isSelectedGender}
                                                                autoComplete="off"
                                                                onChange={(event) => updatePatientField('gender', event.target.value)}
                                                                className="h-4 w-4 accent-[var(--falcon-highlight)]"
                                                            />
                                                            {option.label}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <label className="block space-y-2">
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-[10px] font-black uppercase tracking-widest text-primary">Age</span>
                                                <span className="rounded-full border border-border bg-primary px-3 py-1 text-[11px] font-black text-primary">{userData.age}</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="5"
                                                max="110"
                                                step="1"
                                                value={userData.age}
                                                onChange={(event) => updatePatientField('age', Number(event.target.value))}
                                                className="w-full accent-[var(--falcon-highlight)]"
                                            />
                                            <div className="flex justify-between text-[10px] font-bold text-primary/70">
                                                <span>5</span>
                                                <span>110</span>
                                            </div>
                                        </label>
                                    </div>
                                    {consentFormError && (
                                        <p className="rounded-2xl border border-warning bg-warning px-4 py-3 text-[12px] font-bold text-warning">
                                            {consentFormError}
                                        </p>
                                    )}
                                </div>
                                <button onClick={beginSmileTransformFromConsent} className="px-5 sm:px-8 w-full py-5 rounded-2xl bg-highlight text-highlight text-base sm:text-xl font-black hover:bg-highlight shadow-2xl flex items-center justify-center gap-2 sm:gap-3 transition-transform active:scale-95 whitespace-nowrap"><Sparkles size={22} className="shrink-0" /> Transform My Smile</button>
                                <button
                                    onClick={() => resetPluginToInitialUploadStage()}
                                    className="text-primary rounded-2xl py-5 bg-info-10 hover:text-info font-bold flex items-center justify-center gap-2 py-2"
                                >
                                    <Trash2 size={16} /> Use Different Photo
                                </button>
                            </div>
                        </div>
                    )}

                    {loading && (
                        <div className="flex flex-col items-center justify-center py-12 space-y-6">
                            {renderGenerationProgress({
                                title: 'New Smile Loading',
                                subtitle: 'Please wait while we transform your smile.'
                            })}
                        </div>
                    )}

                    {results.length > 0 && !loading && (
                        <div className="w-full max-w-7xl mx-auto flex flex-col gap-6 sm:gap-8 lg:gap-12">
                            <div className="animate-in fade-in duration-700 space-y-5 sm:space-y-6 pb-20 sm:pb-24">
                                {/* 1. SMILE TRANSFORMATIONS SECTION */}
                                <div className="bg-highlight rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[3rem] p-4 sm:p-5 md:p-10 shadow-2xl">
                                    <div className="flex flex-col min-[1200px]:flex-row justify-between items-start min-[1200px]:items-center gap-3 sm:gap-6 mb-4 sm:mb-6">
                                        <div className="flex w-full flex-col gap-3 min-[1200px]:block min-[1200px]:w-auto">
                                            <div>
                                                <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Smile Transformations</h2>
                                                <p className="text-sm sm:text-base text-white/80 mt-2">AI transformations based on your anatomy.</p>
                                            </div>

                                            {!reviewMode && (
                                                <div className="grid w-full max-w-[11rem] grid-cols-3 gap-2 min-[1200px]:hidden">
                                                    <button
                                                        type="button"
                                                        onClick={() => resetPluginToInitialUploadStage()}
                                                        className="flex min-w-0 flex-col items-center gap-1 text-white transition-all active:scale-95"
                                                        aria-label="Change picture"
                                                        title="Change picture"
                                                    >
                                                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary shadow-lg">
                                                            <Upload size={16} />
                                                        </span>
                                                        <span className="text-[9px] font-black leading-none">Change</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={downloadSmilePackage}
                                                        disabled={isPreparingDownload}
                                                        className="flex min-w-0 flex-col items-center gap-1 text-white transition-all active:scale-95 disabled:opacity-50"
                                                        aria-label={isPreparingDownload ? 'Preparing data' : 'Save data'}
                                                        title={isPreparingDownload ? 'Preparing data' : 'Save data'}
                                                    >
                                                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary shadow-lg">
                                                            {isPreparingDownload ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
                                                        </span>
                                                        <span className="text-[9px] font-black leading-none">Save</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={beginSmileTransformFromConsent}
                                                        disabled={isRegenerating || isPreparingDownload}
                                                        className="flex min-w-0 flex-col items-center gap-1 text-white transition-all active:scale-95 disabled:opacity-50"
                                                        aria-label="Regenerate"
                                                        title="Regenerate"
                                                    >
                                                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary shadow-lg">
                                                            <RefreshCw size={16} className={isRegenerating ? 'animate-spin' : ''} />
                                                        </span>
                                                        <span className="text-[9px] font-black leading-none">Redo</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {!reviewMode && (
                                            <div className="hidden min-[1200px]:flex min-[1200px]:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                                                <button
                                                    onClick={() => resetPluginToInitialUploadStage()}
                                                    className="bg-primary text-primary hover:bg-highlight hover:text-highlight px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl font-black flex items-center justify-center gap-2 shadow-xl transition-all active:scale-95 text-xs sm:text-sm"
                                                >
                                                    Change Picture
                                                </button>
                                                <button
                                                    onClick={downloadSmilePackage}
                                                    disabled={isPreparingDownload}
                                                    className="bg-primary text-primary hover:bg-highlight hover:text-highlight px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl font-black flex items-center justify-center gap-2 shadow-xl transition-all active:scale-95 disabled:opacity-50 whitespace-nowrap text-xs sm:text-sm"
                                                >
                                                    {isPreparingDownload ? <RefreshCw size={18} className="animate-spin" /> : <Download size={18} />}
                                                    {isPreparingDownload ? 'Preparing Data' : 'Save Data'}
                                                </button>
                                                <button
                                                    onClick={beginSmileTransformFromConsent}
                                                    disabled={isRegenerating || isPreparingDownload}
                                                    className="bg-primary text-primary hover:bg-highlight hover:text-highlight px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl font-black flex items-center justify-center gap-2 shadow-xl transition-all active:scale-95 disabled:opacity-50 whitespace-nowrap text-xs sm:text-sm"
                                                >
                                                    {isRegenerating ? <RefreshCw size={18} className="animate-spin" /> : <RefreshCw size={18} />}
                                                    Regenerate
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {(() => {
                                        const smileImages = [
                                            { key: 'original', title: 'Current Smile', label: 'Current Smile', src: image, alt: 'Current smile' },
                                            { key: 'alternative-1', title: 'Alternative 01', label: 'Alternative 1', src: results[0], alt: 'Alternative 1' },
                                            { key: 'alternative-2', title: 'Alternative 02', label: 'Alternative 2', src: results[1], alt: 'Alternative 2' }
                                        ];
                                        const selectedCarouselImage = getSelectedCarouselImage(smileImages, selectedSmileImageKey);

                                        return (
                                            <>
                                                <div className="lg:hidden">
                                                    <div className="overflow-hidden rounded-[2rem] border-4 border-white/80 bg-highlight shadow-2xl">
                                                        <div className="px-4 py-3">
                                                            <span className="inline-flex rounded-3xl border border-white/70 bg-white px-4 py-2 text-xs font-black text-primary shadow-sm">
                                                                {selectedCarouselImage?.title || 'Smile Preview'}
                                                            </span>
                                                        </div>
                                                        <div className="relative bg-highlight">
                                                            {selectedCarouselImage && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openSmileComparisonViewer({ images: smileImages, initialKey: selectedCarouselImage.key })}
                                                                    className="block w-full focus:outline-none focus:ring-4 focus:ring-white/30"
                                                                    aria-label={`Open ${selectedCarouselImage.label}`}
                                                                >
                                                                    <img
                                                                        src={selectedCarouselImage.src}
                                                                        alt={selectedCarouselImage.alt}
                                                                        className="h-80 w-full object-contain"
                                                                    />
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => stepSmileCarousel(smileImages, -1)}
                                                                className="absolute left-3 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-950 shadow-lg"
                                                                aria-label="Previous image"
                                                            >
                                                                <ChevronLeft size={22} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => stepSmileCarousel(smileImages, 1)}
                                                                className="absolute right-3 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-950 shadow-lg"
                                                                aria-label="Next image"
                                                            >
                                                                <ChevronRight size={22} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="mt-3 grid grid-cols-3 gap-3">
                                                        {smileImages.map((item) => (
                                                            <button
                                                                key={`smile-thumb-${item.key}`}
                                                                type="button"
                                                                onClick={() => item.src && setSelectedSmileImageKey(item.key)}
                                                                disabled={!item.src}
                                                                className={`overflow-hidden rounded-xl border-2 bg-highlight transition ${selectedCarouselImage?.key === item.key
                                                                    ? 'border-white'
                                                                    : 'border-white/40'
                                                                    } disabled:opacity-50`}
                                                                aria-label={`Show ${item.label}`}
                                                            >
                                                                {item.src && (
                                                                    <img
                                                                        src={item.src}
                                                                        alt={`${item.label} thumbnail`}
                                                                        className="h-24 w-full object-contain"
                                                                    />
                                                                )}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="hidden gap-5 sm:gap-6 lg:grid lg:grid-cols-3 lg:gap-8 items-start">
                                                    {smileImages.map((item, idx) => (
                                                        <div key={idx} className="bg-highlight border-4 border-white/80 rounded-[2.25rem] py-4 sm:py-5 md:py-6 flex flex-col items-start text-left h-full space-y-4 shadow-2xl overflow-hidden" style={{ maxHeight: '30rem' }}>
                                                            <span className="bg-white text-primary shadow-sm border border-white/70 mx-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-3xl font-black flex items-center justify-center gap-2 shadow-xl transition-all active:scale-95 text-xs sm:text-sm">
                                                                {item.title}
                                                            </span>

                                                            <button
                                                                type="button"
                                                                onClick={() => item.src && openSmileComparisonViewer({ images: smileImages, initialKey: item.key })}
                                                                className="w-full relative  overflow-hidden aspect-[4/5] bg-transparent focus:outline-none focus:ring-4 focus:ring-white/30"
                                                            >
                                                                <img
                                                                    src={item.src}
                                                                    alt={item.alt}
                                                                    className="w-full h-full object-contain"
                                                                />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* {reviewMode && triageData && (
                                <div className="rounded-[2rem] border border-white/15 bg-white/10 p-4 sm:p-5 md:p-6 text-primary mt-5">
                                    <p className="text-xs font-black uppercase tracking-[0.2em] text-primary">Triage analysis</p>
                                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                                        {(triageData.specialists || []).slice(0, 4).map((item, index) => (
                                            <div key={`${item?.name || 'triage'}-${index}`} className="rounded-[1.25rem] border border-white/10 p-4">
                                                <p className="text-sm font-black text-primary">{item?.name || `Finding ${index + 1}`}</p>
                                                <p className="mt-2 text-sm leading-6 text-slate-200">{item?.reason || 'No triage note returned.'}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )} */}

                            {/* 2. PERSONALIZED ASSESSMENT SECTION */}
                            {showPersonalizedAssessment && (
                                <div ref={assessmentRef} style={themeStyle} className="bg-primary text-primary rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[3rem] border border-border shadow-xl p-4 sm:p-5 md:p-10 space-y-5 sm:space-y-6 md:space-y-10">
                                    <h2 className="text-lg sm:text-xl md:text-3xl font-black text-primary tracking-tight">Personalized Assessment</h2>

                                    {/* Specialist Row */}
                                    {activeThemeSettings.showAssessmentSpecialists && (
                                        <div className="grid min-[1200px]:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                                            {displayedSpecialists.length > 0 ? (
                                                displayedSpecialists.map((item, index) => (
                                                    <div key={`care-${index}`} className="p-4 sm:p-5 md:p-8 rounded-[1.5rem] sm:rounded-[1.75rem] md:rounded-[2.5rem] border border-border flex flex-col h-full">
                                                        <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                                                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center border border-border justify-center text-primary shadow-sm shrink-0">
                                                                <Smile size={24} />
                                                            </div>
                                                            <h3 className="text-sm sm:text-base md:text-lg font-black text-primary leading-tight">{item.name} Care</h3>
                                                        </div>
                                                        <p className="text-[13px] sm:text-sm md:text-base leading-relaxed text-primary">{item.reason}</p>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="p-4 sm:p-5 md:p-8 bg-primary-90 rounded-[1.5rem] sm:rounded-[1.75rem] md:rounded-[2.5rem] border border-border text-center col-span-full">
                                                    <p className="font-bold text-primary text-sm sm:text-base md:text-xl">Standard aesthetic care plan</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!reviewMode && visibleEstimateCards.length > 0 && (
                                        <div className="grid min-[1200px]:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                                            {visibleEstimateCards.map((item, index) => (
                                                <div key={`est-${index}`} className="p-4 sm:p-5 md:p-8 bg-primary text-primary rounded-[1.5rem] sm:rounded-[1.75rem] md:rounded-[2.5rem] border border-border flex flex-col h-full">
                                                    <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                                                        <div className="w-9 h-9 md:w-10 md:h-10 border border-border rounded-xl flex items-center justify-center text-primary shadow-sm shrink-0">
                                                            {item.type === 'price' ? <Zap size={20} /> : <Calendar size={20} />}
                                                        </div>
                                                        <h3 className="text-sm sm:text-base md:text-lg font-black text-primary leading-tight">{item.label}</h3>
                                                    </div>
                                                    <p className="text-lg sm:text-xl md:text-2xl font-black text-primary tracking-tight">{item.value}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 3. CHAT & CTA SECTION */}
                            {showAIConsultation && (
                                <div className="bg-primary text-primary rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[3rem] border border-border shadow-xl p-4 sm:p-5 md:p-10 space-y-6 sm:space-y-8 md:space-y-10">

                                    <h2 className="text-xl sm:text-2xl md:text-3xl text-primary font-black tracking-tight">AI Consultation</h2>
                                    <div className="flex-1 overflow-y-auto space-y-4 sm:space-y-6">
                                        {chatMessages.map((msg, i) => (
                                            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`max-w-[85%] flex flex-col gap-4 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                                                    {(() => {
                                                        const chatLinkHref = normalizeChatLinkValue(msg.sourceLink) || normalizeChatLinkValue(msg.linkText);
                                                        const chatLinkLabel = normalizeChatLinkValue(msg.linkText) || normalizeChatLinkValue(msg.sourceLink);

                                                        return (
                                                            <div className={`border border-border w-fit max-w-full p-4 sm:p-5 rounded-[1.35rem] sm:rounded-[1.5rem] text-[13px] sm:text-sm ${msg.role === 'user' ? 'bg-primary-70 text-primary rounded-tr-none shadow-md' : 'bg-primary-70 text-primary  rounded-tl-none shadow-sm'}`}>
                                                                <div className="whitespace-pre-wrap leading-relaxed">
                                                                    {msg.text}
                                                                </div>
                                                                {msg.role === 'bot' && chatLinkHref && chatLinkLabel && (
                                                                    <a
                                                                        href={chatLinkHref}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="mt-3 block break-words font-bold underline underline-offset-4 text-blue-500 hover:opacity-80"
                                                                    >
                                                                        {chatLinkLabel}
                                                                        <SquareArrowOutUpRight size={14} className="inline-block ml-2" />
                                                                    </a>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}

                                                </div>
                                            </div>
                                        ))}

                                        {isChatting && (
                                            <div className="flex justify-start">
                                                <div className="bg-primary text-primary p-4 sm:p-5 rounded-[1.35rem] sm:rounded-[1.5rem] shadow-sm border border-border flex gap-1.5">
                                                    <span className="w-2 h-2 bg-info rounded-full animate-bounce"></span>
                                                    <span className="w-2 h-2 bg-info rounded-full animate-bounce delay-75"></span>
                                                    <span className="w-2 h-2 bg-info rounded-full animate-bounce delay-150"></span>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={chatEndRef} />
                                    </div>

                                    <div className="flex w-full min-w-0 gap-2 border-t border-border bg-primary p-2.5 sm:p-3 pt-4 sm:pt-6 sm:pt-8">
                                        <input
                                            type="text"
                                            value={userInput}
                                            disabled={isChatting}
                                            onChange={(e) => setUserInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                                            placeholder="Ask a question..."
                                            className="min-w-0 flex-1 bg-primary border border-border rounded-2xl px-3 sm:px-4 sm:px-5 py-3 sm:py-4 text-[13px] sm:text-sm outline-none focus:ring-2 focus:ring-border-100 transition-all"
                                        />
                                        <button
                                            onClick={sendMessage}
                                            disabled={!userInput.trim() || isChatting}
                                            className="shrink-0 bg-highlight text-highlight p-3.5 sm:p-4 rounded-2xl hover:bg-highlight transition-all disabled:opacity-50"
                                        >
                                            <Send size={20} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {showLocations && clinicLocations.length > 0 && (
                                <div className={`grid gap-4 sm:gap-6 ${clinicLocations.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                                    {clinicLocations.map((location, locIndex) => {
                                        const mapData = getMapDataForLocation(location, mapZoomLevels[locIndex] || 1);
                                        const phone = getClinicPhoneNumber(location);
                                        const whatsapp = getClinicWhatsappNumber(location);
                                        const phoneHref = getTelHref(phone);
                                        const whatsappHref = getWhatsappHref(whatsapp);
                                        return (
                                            <div key={`clinic-card-${locIndex}`} className="bg-primary text-primary rounded-[1rem] sm:rounded-[1rem] md:rounded-[2rem] border border-border shadow-xl overflow-hidden">
                                                <div className="p-4 sm:p-6 md:p-10 space-y-4 sm:space-y-5">
                                                    <div className="space-y-2">
                                                        <h3 className="text-primary text-xl sm:text-2xl md:text-3xl font-black tracking-tight">
                                                            {location.name || `Location ${locIndex + 1}`}
                                                        </h3>
                                                        {location.address && (
                                                            <p className="text-primary text-[13px] sm:text-sm md:text-base leading-relaxed max-w-2xl">
                                                                {location.address}
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                                                        {mapData ? (
                                                            <div className="flex flex-col items-center gap-2">
                                                                <button type="button" onClick={() => window.open(mapData.openUrl, '_blank', 'noopener')} className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-[1rem] bg-info text-info font-black transition-all shadow-xl hover:bg-highlight focus:outline-none" aria-label="Open clinic location in maps" title="Open Map">
                                                                    <Navigation size={20} className="sm:w-6 sm:h-6" />
                                                                </button>
                                                                <span className="text-[10px] font-black uppercase tracking-widest text-primary">Navigate</span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-[1rem] bg-primary border border-border text-primary">-</div>
                                                        )}
                                                        {showFixedAppointment && !showAppointmentForm && bookingFlow !== 'confirmed' && (
                                                            <div className="flex flex-col items-center gap-2">
                                                                <button type="button" onClick={() => { setBookingFlow('form'); setSelectedLocationIndex(locIndex); }} className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-[1rem] bg-highlight text-highlight font-black transition-all shadow-xl hover:bg-highlight focus:outline-none" aria-label="Book consultation" title="Book Consultation">
                                                                    <Calendar size={20} className="sm:w-6 sm:h-6" />
                                                                </button>
                                                                <span className="text-[10px] font-black uppercase tracking-widest text-primary">Book</span>
                                                            </div>
                                                        )}
                                                        {phoneHref && (
                                                            <div className="flex flex-col items-center gap-2">
                                                                <a href={phoneHref} className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-[1rem] bg-primary text-primary border border-border font-black transition-all shadow-xl hover:bg-info hover:text-info-text focus:outline-none" aria-label={`Call clinic at ${phone}`} title="Call Clinic">
                                                                    <Phone size={20} className="sm:w-6 sm:h-6" />
                                                                </a>
                                                                <span className="text-[10px] font-black uppercase tracking-widest text-primary">Call</span>
                                                            </div>
                                                        )}
                                                        {whatsappHref && (
                                                            <div className="flex flex-col items-center gap-2">
                                                                <a href={whatsappHref} target="_blank" rel="noreferrer" className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-[1rem] bg-[#25D366] text-white border border-[#1fb85a] font-black transition-all shadow-xl hover:brightness-95 focus:outline-none" aria-label={`Send WhatsApp message to clinic at ${whatsapp}`} title="Send WhatsApp">
                                                                    <WhatsAppLogo size={20} className="sm:w-6 sm:h-6" />
                                                                </a>
                                                                <span className="text-[10px] font-black uppercase tracking-widest text-primary">WhatsApp</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div data-falcon-map={`loc-${locIndex}`}
                                                    className="group relative h-[320px] md:h-[480px] border-t border-border bg-screen overflow-hidden"
                                                    onWheel={(event) => handleMapWheel(locIndex, event)}
                                                >
                                                    {mapData ? (
                                                        <>
                                                            <iframe
                                                                title={location.name || `Clinic map ${locIndex + 1}`}
                                                                src={mapData.embedUrl}
                                                                className="h-full w-full border-0"
                                                                loading="lazy"
                                                                referrerPolicy="no-referrer-when-downgrade"
                                                            />
                                                            <div className="pointer-events-none absolute inset-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/20 via-transparent to-transparent p-3 md:p-4">
                                                                <span className="rounded-full border border-border bg-primary/90 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-primary shadow-lg">
                                                                    Ctrl + scroll to zoom
                                                                </span>
                                                                <span className="rounded-full border border-border bg-primary/90 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-primary shadow-lg">
                                                                    Zoom {Math.round((1 / (mapZoomLevels[locIndex] || 1)) * 100)}%
                                                                </span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="h-full w-full flex items-center justify-center text-sm text-primary">Map unavailable</div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {showFixedAppointment && showAppointmentForm && bookingFlow !== 'confirmed' && (
                                <div
                                    ref={bookingSectionRef}
                                    className="bg-primary text-primary rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[3rem] border border-border shadow-xl p-4 sm:p-6 md:p-12 space-y-5 sm:space-y-6 md:space-y-8 shadow-2xl relative overflow-hidden"
                                >
                                    <div className="space-y-2 text-center md:text-left">
                                        <p className="text-[10px] sm:text-[12px] md:text-[14px] font-bold text-highlight uppercase tracking-widest">Complete Your Booking Details</p>
                                        <h3 className="text-primary text-xl sm:text-2xl md:text-4xl font-black tracking-tight">Let&apos;s Schedule an Appointment</h3>
                                        <p className="text-primary text-[13px] sm:text-base md:text-lg max-w-2xl">Enter your name, email address, phone number, and preferred appointment windows below. Once confirmed, our team will receive your booking details right away.</p>
                                    </div>

                                    <div className={`grid gap-2 sm:gap-3 ${appointmentClinicLocations.length > 0 ? 'lg:grid-cols-4' : 'md:grid-cols-3'}`}>
                                        <input
                                            type="text"
                                            value={userData.name}
                                            disabled={isConfirmingAppointment}
                                            onChange={(e) => updateAppointmentField('name', e.target.value)}
                                            placeholder="Full name"
                                            className="w-full bg-primary border border-border rounded-2xl px-4 sm:px-5 py-3 sm:py-4 text-[13px] sm:text-sm outline-none focus:ring-2 focus:ring-border-100 transition-all"
                                        />
                                        <input
                                            type="email"
                                            value={userData.contact}
                                            disabled={isConfirmingAppointment}
                                            onChange={(e) => updateAppointmentField('contact', e.target.value)}
                                            placeholder="Email address"
                                            className="w-full bg-primary border border-border rounded-2xl px-4 sm:px-5 py-3 sm:py-4 text-[13px] sm:text-sm outline-none focus:ring-2 focus:ring-border-100 transition-all"
                                        />
                                        <input
                                            type="tel"
                                            value={userData.phone}
                                            disabled={isConfirmingAppointment}
                                            onChange={(e) => updateAppointmentField('phone', e.target.value)}
                                            placeholder="Phone number (optional)"
                                            className="w-full bg-primary border border-border rounded-2xl px-4 sm:px-5 py-3 sm:py-4 text-[13px] sm:text-sm outline-none focus:ring-2 focus:ring-border-100 transition-all"
                                        />
                                        {appointmentClinicLocations.length > 0 && (
                                            <select
                                                aria-label="Clinic Location"
                                                value={Math.min(selectedLocationIndex, appointmentClinicLocations.length - 1)}
                                                onChange={(event) => setSelectedLocationIndex(Number(event.target.value))}
                                                disabled={isConfirmingAppointment}
                                                className="w-full bg-primary border border-border rounded-2xl px-4 sm:px-5 py-3 sm:py-4 text-[13px] sm:text-sm font-bold outline-none focus:ring-2 focus:ring-border-100 transition-all"
                                            >
                                                {appointmentClinicLocations.map((location, index) => (
                                                    <option key={`appointment-location-${index}`} value={index}>
                                                        {getClinicLocationLabel(location, index)}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>

                                    {isConfirmingAppointment && (
                                        <div className="flex items-center gap-3 rounded-xl border border-border bg-primary p-3 text-primary">
                                            <RefreshCw size={16} className="animate-spin text-highlight" />
                                            <span className="text-xs font-black">Confirming your appointment details...</span>
                                        </div>
                                    )}

                                    {appointmentFormError && (
                                        <div className="flex items-start gap-3 rounded-2xl border border-warning bg-warning px-4 py-3">
                                            <AlertCircle size={18} className="text-warning mt-0.5 shrink-0" />
                                            <p className="text-sm font-bold text-warning leading-relaxed">
                                                {appointmentFormError}
                                            </p>
                                        </div>
                                    )}

                                    <div className="space-y-3">
                                        {preferredSlots.map((slot, index) => (
                                            <div key={index} className="flex flex-col md:flex-row md:items-center gap-3">
                                                <span className="text-[10px] font-black uppercase tracking-widest text-primary md:w-28 shrink-0">
                                                    Preference {index + 1}
                                                </span>
                                                <select
                                                    value={slot.day}
                                                    onChange={(event) => updatePreferredSlot(index, 'day', event.target.value)}
                                                    disabled={isConfirmingAppointment}
                                                    className="min-w-0 flex-1 p-3 text-[12px] font-bold border border-border rounded-xl bg-primary text-primary outline-none focus:ring-2 focus:ring-border-100 transition-all"
                                                >
                                                    {availableDays.map((day, optionIndex) => (
                                                        <option key={`${day}-${optionIndex}`} value={day}>{day}</option>
                                                    ))}
                                                </select>
                                                <select
                                                    value={slot.time}
                                                    onChange={(event) => updatePreferredSlot(index, 'time', event.target.value)}
                                                    disabled={isConfirmingAppointment}
                                                    className="min-w-0 flex-1 p-3 text-[12px] font-bold border border-border rounded-xl bg-primary text-primary outline-none focus:ring-2 focus:ring-border-100 transition-all"
                                                >
                                                    {availableHours.map((time, optionIndex) => (
                                                        <option key={`${time}-${optionIndex}`} value={time}>{time}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex flex-col gap-3 md:flex-row md:justify-end">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setBookingFlow('idle');
                                                setAppointmentFormError("");
                                                queueParentHeightUpdate();
                                            }}
                                            disabled={isConfirmingAppointment}
                                            className="w-full md:w-auto px-6 py-4 border border-border text-primary rounded-2xl font-black text-sm hover:bg-info hover:text-info-text shadow-lg transition-all disabled:opacity-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmAppointment}
                                            disabled={isConfirmingAppointment || !availableDays.length || !availableHours.length}
                                            className="w-full md:w-auto px-6 py-4 bg-highlight text-highlight rounded-2xl font-black text-sm hover:bg-highlight shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-50"
                                        >
                                            {isConfirmingAppointment
                                                ? 'Confirming Booking...'
                                                : availableDays.length && availableHours.length
                                                    ? 'Confirm Bookings'
                                                    : 'Clinic slots unavailable'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {showFixedAppointment && bookingFlow === 'confirmed' && (
                                <div className="bg-primary text-primary rounded-[2rem] md:rounded-[3rem] border border-border shadow-xl p-6 sm:p-8 md:p-12 space-y-6 shadow-2xl relative overflow-hidden">
                                    <div className="space-y-3 text-center md:text-left">
                                        <p className="text-[14px] font-bold text-highlight uppercase tracking-widest">Booking Confirmed</p>
                                        <h3 className="text-primary text-2xl md:text-4xl font-black tracking-tight">Your booking details have been shared with our team</h3>
                                        <p className="text-primary text-base md:text-lg max-w-2xl">
                                            Thank you <span className="font-bold">{userData.name ? `, ${userData.name}` : ''}</span>. Our reception team will reach out to you at <span className="font-bold">{userData.contact || 'your provided email'}</span> or <span className="font-bold">{userData.phone || 'your phone number'}</span> shortly to finalize the appointment.
                                        </p>
                                    </div>

                                    {!!userData.slots?.length && (
                                        <div className="grid gap-3 md:grid-cols-3">
                                            {userData.slots.map((slot, index) => (
                                                <div key={`${slot}-${index}`} className="rounded-2xl border border-border bg-highlight p-4 text-center">
                                                    <p className="text-[10px] font-black uppercase tracking-widest text-highlight">Preference {index + 1}</p>
                                                    <p className="text-sm font-bold text-highlight mt-2">{slot}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                </div>

                    {viewerImage && (
                        <div
                            className="w-full rounded-[1.35rem] sm:rounded-[2rem] bg-slate-950 p-3 sm:p-5 shadow-2xl"
                            role="region"
                            aria-label="Image preview"
                        >
                            <div
                                className="relative mx-auto flex w-full max-w-6xl flex-col overflow-hidden rounded-[1.35rem] sm:rounded-[2rem] border border-white/10 bg-black/35 shadow-[0_30px_80px_rgba(0,0,0,0.55)]"
                            >
                                <button
                                    type="button"
                                    onClick={closeSmileComparisonViewer}
                                    className="absolute right-3 top-3 sm:right-4 sm:top-4 z-10 rounded-full bg-white/90 px-3 py-2 text-xs sm:text-sm font-black text-slate-950 shadow-lg transition hover:bg-white"
                                    aria-label="Close image preview"
                                >
                                    Close
                                </button>

                                <div className="border-b border-white/10 bg-white/5 px-4 py-4 pr-20 sm:px-5 sm:pr-24">
                                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                        <div className="min-w-0">
                                            <p className="text-[11px] sm:text-sm font-black uppercase tracking-[0.2em] sm:tracking-[0.25em] text-cyan-200">Image preview</p>
                                            <h2 className="mt-1 text-xl sm:text-2xl font-black leading-tight text-white">Compare the smile images</h2>
                                            <p className="mt-1 max-w-2xl text-[12px] sm:text-sm leading-snug text-slate-200">
                                                Switch between a single image, two images side by side, or all three images at once.
                                            </p>
                                        </div>

                                        <div className="grid w-full grid-cols-3 gap-1.5 rounded-[0.95rem] bg-white/10 p-1.5 sm:inline-flex sm:w-auto sm:flex-wrap sm:gap-2 sm:rounded-[1.1rem] sm:p-2">
                                            {['single', 'two', 'three'].map((mode) => (
                                                <button
                                                    key={mode}
                                                    type="button"
                                                    onClick={() => setViewerMode(mode)}
                                                    className={`rounded-[0.75rem] sm:rounded-[0.9rem] px-2 sm:px-4 py-2 text-[12px] sm:text-sm font-black capitalize transition ${viewerImage.mode === mode
                                                        ? 'bg-white text-slate-950 shadow-lg'
                                                        : 'bg-transparent text-white hover:bg-white/10'
                                                        }`}
                                                >
                                                    {mode === 'single' ? '1 Image' : mode === 'two' ? '2 Images' : 'All Images'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3 sm:p-5">
                                    {viewerImage.mode === 'single' && (
                                        <div className="space-y-4">
                                            {renderViewerPicker('Single image', viewerImage.singleKey, (key) => updateViewerSlot('single', key))}
                                            {renderViewerImagePanel(viewerImage.singleKey, 'min-h-[260px] sm:min-h-[420px]', 'max-h-[58vh] sm:max-h-[72vh]')}
                                        </div>
                                    )}

                                    {viewerImage.mode === 'two' && (
                                        <div className="space-y-4">
                                            <div className="grid gap-4 xl:grid-cols-2">
                                                {renderViewerPicker('Left image', viewerImage.leftKey, (key) => updateViewerSlot('left', key))}
                                                {renderViewerPicker('Right image', viewerImage.rightKey, (key) => updateViewerSlot('right', key))}
                                            </div>

                                            <div className="grid gap-4 xl:grid-cols-2">
                                                {renderViewerImagePanel(viewerImage.leftKey, 'min-h-[240px] sm:min-h-[360px]', 'max-h-[54vh] sm:max-h-[65vh]')}
                                                {renderViewerImagePanel(viewerImage.rightKey, 'min-h-[240px] sm:min-h-[360px]', 'max-h-[54vh] sm:max-h-[65vh]')}
                                            </div>
                                        </div>
                                    )}

                                    {viewerImage.mode === 'three' && (
                                        <div className="space-y-4">
                                            <div className="grid gap-4 xl:grid-cols-3">
                                                {viewerImage.images.map((item) => (
                                                    <div
                                                        key={item.key}
                                                        className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-black/25"
                                                    >
                                                        <div className="border-b border-white/10 px-4 py-3 text-[11px] sm:text-sm font-black uppercase tracking-[0.16em] sm:tracking-[0.2em] text-cyan-200">
                                                            {item.label}
                                                        </div>
                                                        <div className="flex min-h-[220px] sm:min-h-[360px] items-center justify-center p-4">
                                                            <img
                                                                src={item.src}
                                                                alt={item.label}
                                                                className="max-h-[52vh] sm:max-h-[65vh] max-w-full object-contain"
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-screen text-info backdrop-blur-sm">
                            <div className="bg-primary text-primary rounded-[2.5rem] shadow-2xl max-w-lg w-full overflow-hidden">
                                <div className="bg-warning p-6 text-warning flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <AlertCircle size={28} />
                                        <div>
                                            <p className="text-lg font-black">Something went wrong</p>
                                            <p className="text-sm">Please try again.</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setError(null)} className="p-2 rounded-lg border border-border bg-primary text-primary transition-all">  <X size={20} /></button>
                                </div>
                                <div className="p-8 space-y-6">
                                    <p className="text-primary leading-relaxed my-6">{error}</p>
                                    <button onClick={() => setError(null)} className="w-full py-4 rounded-2xl border border-border text-primary font-black transition-all">Close</button>
                                </div>
                            </div>
                        </div>
                    )}
            </div>
        </div >
    );
};

export default FalconSmileAppointmentProposal;
